<template>
  <div class="leveling-container" style="width: 100%;">
    <!-- Loading overlay -->
    <div v-if="componentState.isLoading" class="loading-overlay">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      <p class="mt-4">Loading levelling configuration...</p>
    </div>
    
    <v-card flat style="width: 100%;" :class="{ 'loading-disabled': componentState.isLoading }">
      <v-card-title class="compact-header py-2 px-3">
        <v-icon left color="primary" small>mdi-stairs-up</v-icon>
        <span class="text-h6">Leveling Configuration</span>
        <v-spacer></v-spacer>
        <v-btn
          v-if="componentState.levelingConfig.client_framework_config"
          color="primary"
          small
          @click="saveLevelingData"
          :loading="componentState.savingLeveling"
          :disabled="componentState.isLoading"
        >
          <v-icon left small>mdi-content-save</v-icon>
          <span v-if="componentState.savingLeveling">Processing...</span>
          <span v-else>Save Leveling Data</span>
        </v-btn>
      </v-card-title>
      <v-card-text class="pa-3" style="width: 100%; max-width: 100%;">
        
        <!-- Initial Configuration Screen -->
        <div v-if="!componentState.levelingConfig.client_framework_config" class="initial-config-container">
          <div class="config-header">
            <v-icon size="48" color="primary" class="mb-3">mdi-stairs-up</v-icon>
            <h2 class="config-title">Setup Leveling Framework</h2>
            <p class="config-subtitle">
              Configure how your organization's leveling system will work. Choose a framework type and data source to get started.
            </p>
          </div>

          <v-form ref="configForm" v-model="componentState.configFormValid" class="config-form">
            <!-- Framework Selection -->
            <v-card outlined class="config-section-card mb-4">
              <v-card-text class="pa-4">
                <h3 class="config-section-title mb-3">
                  <v-icon left color="primary" size="20">mdi-cog</v-icon>
                  Framework Type
                </h3>
                <v-radio-group 
                  v-model="componentState.newConfig.framework_value" 
                  :rules="[v => !!v || 'Framework type is required']"
                  class="framework-selection mt-0"
                  hide-details="auto"
                >
                  <v-radio 
                    value="RoleMapper" 
                    color="primary"
                    class="framework-option mb-3"
                  >
                    <template v-slot:label>
                      <div class="framework-option-content">
                        <div class="framework-option-title">RoleMapper Framework</div>
                        <div class="framework-option-description">Use predefined role progression paths with industry-standard levels</div>
                      </div>
                    </template>
                  </v-radio>
                  <v-radio 
                    value="Custom" 
                    color="primary"
                    class="framework-option"
                  >
                    <template v-slot:label>
                      <div class="framework-option-content">
                        <div class="framework-option-title">Custom Framework</div>
                        <div class="framework-option-description">Create your own leveling structure tailored to your organization</div>
                      </div>
                    </template>
                  </v-radio>
                </v-radio-group>
              </v-card-text>
            </v-card>

            <!-- Data Source Selection -->
            <v-card outlined class="config-section-card mb-4">
              <v-card-text class="pa-4">
                <h3 class="config-section-title mb-3">
                  <v-icon left color="primary" size="20">mdi-database</v-icon>
                  Data Source
                </h3>
                <v-radio-group 
                  v-model="componentState.newConfig.type" 
                  :rules="[v => !!v || 'Data source is required']"
                  class="data-source-selection mt-0"
                  hide-details="auto"
                >
                  <v-radio 
                    value="hr" 
                    color="primary"
                    class="data-source-option mb-3"
                  >
                    <template v-slot:label>
                      <div class="data-source-option-content">
                        <div class="data-source-option-title">
                          <v-icon left small color="primary">mdi-sitemap</v-icon>
                          Hierarchy
                        </div>
                        <div class="data-source-option-description">Map levels to existing organizational hierarchies</div>
                      </div>
                    </template>
                  </v-radio>
                  <v-radio 
                    value="tp_id" 
                    color="primary"
                    class="data-source-option"
                  >
                    <template v-slot:label>
                      <div class="data-source-option-content">
                        <div class="data-source-option-title">
                          <v-icon left small color="primary">mdi-file-document-outline</v-icon>
                          Template Part
                        </div>
                        <div class="data-source-option-description">Use template parts and attributes for level determination</div>
                      </div>
                    </template>
                  </v-radio>
                </v-radio-group>
              </v-card-text>
            </v-card>

            <!-- Hierarchy Configuration -->
            <v-card v-if="componentState.newConfig.type === 'hr'" outlined class="config-section-card mb-4">
              <v-card-text class="pa-4">
                <h3 class="config-section-title mb-3">
                  <v-icon left color="primary" size="20">mdi-sitemap</v-icon>
                  Hierarchy Configuration
                </h3>
                <v-select
                  v-model="componentState.newConfig.ht_id"
                  :items="hierarchyOptions"
                  item-text="ht_name"
                  item-value="ht_id"
                  label="Select Hierarchy"
                  outlined
                  hide-details
                  dense
                  :rules="[v => !!v || 'Hierarchy selection is required']"
                  class="mb-0"
                >
                  <template v-slot:selection="{ item }">
                    {{ item.ht_name }}
                  </template>
                  <template v-slot:item="{ item }">
                    <div class="hierarchy-item">
                      <div class="hierarchy-item-name">{{ item.ht_name }}</div>
                    </div>
                  </template>
                </v-select>

                <!-- Level Field Selection -->
                <div v-if="componentState.newConfig.ht_id" class="level-field-section">
                  <h4 class="config-subsection-title mb-2">
                    <v-icon left color="grey darken-1" size="16">mdi-format-list-numbered</v-icon>
                    Level Field
                  </h4>
                  <p class="subsection-description mb-2">
                    Choose which field from the hierarchy will be used to determine the level.
                  </p>
                  <v-select
                    v-model="componentState.newConfig.ht_level"
                    :items="selectedHierarchyLevels"
                    item-text="name"
                    item-value="index"
                    label="Select Level Field"
                    outlined
                    hide-details
                    dense
                    :rules="[v => !!v || 'Level field selection is required']"
                  >
                    <template v-slot:selection="{ item }">
                      {{ item.name }}
                    </template>
                    <template v-slot:item="{ item }">
                      <div class="level-item">
                        <div class="level-item-name">{{ item.name }}</div>
                      </div>
                    </template>
                  </v-select>
                </div>
              </v-card-text>
            </v-card>

            <!-- Template Part Configuration -->
            <v-card v-if="componentState.newConfig.type === 'tp_id'" outlined class="config-section-card mb-4">
              <v-card-text class="pa-4">
                <h3 class="config-section-title mb-3">
                  <v-icon left color="primary" size="20">mdi-file-document-outline</v-icon>
                  Template Part Configuration
                </h3>
                <p class="subsection-description mb-3">
                  Configure which template parts will be used for level determination. Select document types and their template parts/attributes.
                </p>

                <div class="template-config-container">
                  <div
                    v-for="(config, index) in componentState.newConfig.template_configs"
                    :key="index"
                    class="template-config-item"
                  >
                    <v-card outlined class="template-config-card mb-3">
                      <v-card-text class="pa-3">
                        <div class="d-flex align-center justify-space-between mb-2">
                          <h5 class="config-item-title">Configuration {{ index + 1 }}</h5>
                          <v-btn
                            icon
                            x-small
                            color="error"
                            @click="removeTemplateConfig(index)"
                            :disabled="componentState.newConfig.template_configs.length === 1"
                          >
                            <v-icon x-small>mdi-delete</v-icon>
                          </v-btn>
                        </div>

                        <v-row dense>
                          <v-col cols="12">
                            <v-select
                              v-model="config.tmpl_id"
                              :items="docTypeOptions"
                              item-text="tmpl_name"
                              item-value="tmpl_id"
                              label="Document Type"
                              outlined
                              dense
                              :rules="[v => !!v || 'Document Type is required']"
                              hide-details="auto"
                              @change="onDocTypeChange(index)"
                            >
                              <template v-slot:selection="{ item }">
                                <v-icon left small>mdi-file-document</v-icon>
                                {{ item.tmpl_name }}
                              </template>
                              <template v-slot:item="{ item }">
                                <v-icon left small>mdi-file-document</v-icon>
                                {{ item.tmpl_name }}
                              </template>
                            </v-select>
                          </v-col>
                        </v-row>

                        <v-row v-if="config.tmpl_id" dense class="mt-2">
                          <v-col cols="12" md="6">
                            <v-select
                              v-model="config.tp_id"
                              :items="getTemplatePartsForConfig(index)"
                              item-text="name"
                              item-value="tp_id"
                              label="Template Part"
                              outlined
                              dense
                              :rules="[v => !!v || 'Template Part is required']"
                              hide-details="auto"
                              :loading="config.loadingParts"
                              @change="onTemplatePartChange(index)"
                            >
                              <template v-slot:selection="{ item }">
                                {{ item.name }}
                              </template>
                              <template v-slot:item="{ item }">
                                {{ item.name }}
                              </template>
                            </v-select>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-select
                              v-if="getAttributesForConfig(index).length > 0"
                              v-model="config.tpa_id"
                              :items="getAttributesForConfig(index)"
                              item-text="name"
                              item-value="tpa_id"
                              label="Attribute (Optional)"
                              outlined
                              dense
                              hide-details="auto"
                              clearable
                            >
                              <template v-slot:selection="{ item }">
                                {{ item.title }}
                              </template>
                              <template v-slot:item="{ item }">
                                {{ item.title }}
                              </template>
                            </v-select>
                            <v-text-field
                              v-else
                              value="No attributes available"
                              label="Attribute (Optional)"
                              outlined
                              dense
                              hide-details="auto"
                              disabled
                            ></v-text-field>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </div>

                  <v-btn
                    color="primary"
                    outlined
                    small
                    @click="addTemplateConfig"
                    class="add-config-btn"
                  >
                    <v-icon left x-small>mdi-plus</v-icon>
                    Add Configuration
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>

            <!-- Track Source Configuration (Optional) -->
            <v-card v-if="componentState.newConfig.type" outlined class="config-section-card mb-4">
              <v-card-text class="pa-4">
                <div class="d-flex align-center mb-3">
                  <v-checkbox
                    v-model="componentState.newConfig.track_enabled"
                    hide-details
                    class="mt-0 pt-0 mr-2"
                  ></v-checkbox>
                  <h3 class="config-section-title" style="margin-bottom: 0;">
                    <v-icon left color="primary" size="20">mdi-source-branch</v-icon>
                    Track Source (Optional)
                  </h3>
                </div>
                <p class="subsection-description mb-3">
                  Optionally configure a separate data source to determine the track/type for each mapping.
                </p>

                <div v-if="componentState.newConfig.track_enabled">
                  <!-- Track Data Source Selection -->
                  <v-radio-group
                    v-model="componentState.newConfig.track_type"
                    class="data-source-selection mt-0 mb-3"
                    hide-details="auto"
                  >
                    <v-radio
                      value="hr"
                      color="primary"
                      class="data-source-option mb-3"
                    >
                      <template v-slot:label>
                        <div class="data-source-option-content">
                          <div class="data-source-option-title">
                            <v-icon left small color="primary">mdi-sitemap</v-icon>
                            Hierarchy
                          </div>
                          <div class="data-source-option-description">Map tracks to an organizational hierarchy</div>
                        </div>
                      </template>
                    </v-radio>
                    <v-radio
                      value="tp_id"
                      color="primary"
                      class="data-source-option"
                    >
                      <template v-slot:label>
                        <div class="data-source-option-content">
                          <div class="data-source-option-title">
                            <v-icon left small color="primary">mdi-file-document-outline</v-icon>
                            Template Part
                          </div>
                          <div class="data-source-option-description">Use template parts and attributes for track determination</div>
                        </div>
                      </template>
                    </v-radio>
                  </v-radio-group>

                  <!-- Track Hierarchy Configuration -->
                  <v-card v-if="componentState.newConfig.track_type === 'hr'" outlined class="config-section-card mb-3">
                    <v-card-text class="pa-4">
                      <h4 class="config-subsection-title mb-2">
                        <v-icon left color="grey darken-1" size="16">mdi-sitemap</v-icon>
                        Track Hierarchy Configuration
                      </h4>
                      <v-select
                        v-model="componentState.newConfig.track_ht_id"
                        :items="hierarchyOptions"
                        item-text="ht_name"
                        item-value="ht_id"
                        label="Select Hierarchy"
                        outlined
                        hide-details
                        dense
                        class="mb-0"
                      >
                        <template v-slot:selection="{ item }">
                          {{ item.ht_name }}
                        </template>
                        <template v-slot:item="{ item }">
                          <div class="hierarchy-item">
                            <div class="hierarchy-item-name">{{ item.ht_name }}</div>
                          </div>
                        </template>
                      </v-select>

                      <div v-if="componentState.newConfig.track_ht_id" class="level-field-section">
                        <h4 class="config-subsection-title mb-2">
                          <v-icon left color="grey darken-1" size="16">mdi-format-list-numbered</v-icon>
                          Track Field
                        </h4>
                        <p class="subsection-description mb-2">
                          Choose which field from the hierarchy will be used to determine the track.
                        </p>
                        <v-select
                          v-model="componentState.newConfig.track_ht_level"
                          :items="selectedTrackHierarchyLevels"
                          item-text="name"
                          item-value="index"
                          label="Select Track Field"
                          outlined
                          hide-details
                          dense
                        >
                          <template v-slot:selection="{ item }">
                            {{ item.name }}
                          </template>
                          <template v-slot:item="{ item }">
                            <div class="level-item">
                              <div class="level-item-name">{{ item.name }}</div>
                            </div>
                          </template>
                        </v-select>
                      </div>
                    </v-card-text>
                  </v-card>

                  <!-- Track Template Part Configuration -->
                  <v-card v-if="componentState.newConfig.track_type === 'tp_id'" outlined class="config-section-card mb-3">
                    <v-card-text class="pa-4">
                      <h4 class="config-subsection-title mb-2">
                        <v-icon left color="grey darken-1" size="16">mdi-file-document-outline</v-icon>
                        Track Template Part Configuration
                      </h4>
                      <p class="subsection-description mb-3">
                        Configure which template parts will be used for track determination.
                      </p>

                      <div class="template-config-container">
                        <div
                          v-for="(config, index) in componentState.newConfig.track_template_configs"
                          :key="'track-' + index"
                          class="template-config-item"
                        >
                          <v-card outlined class="template-config-card mb-3">
                            <v-card-text class="pa-3">
                              <div class="d-flex align-center justify-space-between mb-2">
                                <h5 class="config-item-title">Configuration {{ index + 1 }}</h5>
                                <v-btn
                                  icon
                                  x-small
                                  color="error"
                                  @click="removeTrackTemplateConfig(index)"
                                  :disabled="componentState.newConfig.track_template_configs.length === 1"
                                >
                                  <v-icon x-small>mdi-delete</v-icon>
                                </v-btn>
                              </div>

                              <v-row dense>
                                <v-col cols="12">
                                  <v-select
                                    v-model="config.tmpl_id"
                                    :items="docTypeOptions"
                                    item-text="tmpl_name"
                                    item-value="tmpl_id"
                                    label="Document Type"
                                    outlined
                                    dense
                                    hide-details="auto"
                                    @change="onTrackDocTypeChange(index)"
                                  >
                                    <template v-slot:selection="{ item }">
                                      <v-icon left small>mdi-file-document</v-icon>
                                      {{ item.tmpl_name }}
                                    </template>
                                    <template v-slot:item="{ item }">
                                      <v-icon left small>mdi-file-document</v-icon>
                                      {{ item.tmpl_name }}
                                    </template>
                                  </v-select>
                                </v-col>
                              </v-row>

                              <v-row v-if="config.tmpl_id" dense class="mt-2">
                                <v-col cols="12" md="6">
                                  <v-select
                                    v-model="config.tp_id"
                                    :items="getTrackTemplatePartsForConfig(index)"
                                    item-text="name"
                                    item-value="tp_id"
                                    label="Template Part"
                                    outlined
                                    dense
                                    hide-details="auto"
                                    :loading="config.loadingParts"
                                    @change="onTrackTemplatePartChange(index)"
                                  >
                                    <template v-slot:selection="{ item }">
                                      {{ item.name }}
                                    </template>
                                    <template v-slot:item="{ item }">
                                      {{ item.name }}
                                    </template>
                                  </v-select>
                                </v-col>
                                <v-col cols="12" md="6">
                                  <v-select
                                    v-if="getTrackAttributesForConfig(index).length > 0"
                                    v-model="config.tpa_id"
                                    :items="getTrackAttributesForConfig(index)"
                                    item-text="name"
                                    item-value="tpa_id"
                                    label="Attribute (Optional)"
                                    outlined
                                    dense
                                    hide-details="auto"
                                    clearable
                                  >
                                    <template v-slot:selection="{ item }">
                                      {{ item.title }}
                                    </template>
                                    <template v-slot:item="{ item }">
                                      {{ item.title }}
                                    </template>
                                  </v-select>
                                  <v-text-field
                                    v-else
                                    value="No attributes available"
                                    label="Attribute (Optional)"
                                    outlined
                                    dense
                                    hide-details="auto"
                                    disabled
                                  ></v-text-field>
                                </v-col>
                              </v-row>
                            </v-card-text>
                          </v-card>
                        </div>

                        <v-btn
                          color="primary"
                          outlined
                          small
                          @click="addTrackTemplateConfig"
                          class="add-config-btn"
                        >
                          <v-icon left x-small>mdi-plus</v-icon>
                          Add Configuration
                        </v-btn>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </v-card-text>
            </v-card>

            <!-- Action Buttons -->
            <div class="config-actions">
              <v-btn
                large
                color="primary"
                @click="createFrameworkConfig"
                :loading="componentState.savingConfig"
                :disabled="!componentState.configFormValid || !isConfigValid"
                class="create-button"
              >
                <v-icon left>mdi-check</v-icon>
                <span v-if="componentState.savingConfig">Saving...</span>
                <span v-else>{{ isEditMode ? 'Update Framework' : 'Create Framework' }}</span>
              </v-btn>
            </div>
          </v-form>
        </div>

        <!-- Existing Configuration Display -->
        <div v-else>
          <!-- Compact Info Bar -->
          <div class="info-bar mb-3">
            <div v-if="componentState.levelingConfig.client_framework_config?.type === 'hr'" class="info-item">
              <span class="info-label">Hierarchy:</span>
              <v-chip color="primary" x-small label class="ml-2">
                <v-icon left x-small>mdi-sitemap</v-icon>
                {{ getHierarchyName(componentState.levelingConfig.client_framework_config.ht_id) }}
              </v-chip>
              <!-- Warning chip for unmapped hierarchy values -->
              <v-menu
                v-if="unmappedHierarchyCount > 0"
                open-on-hover
                offset-x
                max-width="400px"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    color="warning"
                    x-small
                    class="ml-2"
                    outlined
                    v-bind="attrs"
                    v-on="on"
                    title="Hierarchy values not mapped to any level"
                  >
                    <v-icon left x-small color="warning">mdi-alert</v-icon>
                    {{ unmappedHierarchyCount }} unmapped
                  </v-chip>
                </template>
                <v-card max-width="400px">
                  <v-card-title class="py-2 px-3">
                    <span class="text-subtitle-2 font-weight-bold">Unmapped Hierarchy Values</span>
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="pa-3">
                    <div v-for="(val, idx) in unmappedHierarchyValues" :key="idx" style="margin-bottom: 8px;">
                      <v-chip x-small color="grey lighten-1" class="mr-2">{{ val }}</v-chip>
                    </div>
                    <div v-if="unmappedHierarchyValues.length === 0" class="grey--text text-caption">
                      All hierarchy values are mapped.
                    </div>
                  </v-card-text>
                </v-card>
              </v-menu>
            </div>
            <div v-if="componentState.levelingConfig.client_framework_config?.type === 'tp_id'" class="info-item">
              <span class="info-label">Template Parts:</span>
              <v-menu
                open-on-hover
                offset-x
                max-width="500px"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    color="primary"
                    x-small
                    label
                    class="ml-2"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon left x-small>mdi-file-document-outline</v-icon>
                    {{ templateConfigCount }} configuration{{ templateConfigCount !== 1 ? 's' : '' }}
                  </v-chip>
                </template>
                <v-card max-width="500px">
                  <v-card-title class="py-2 px-3">
                    <span class="text-subtitle-2 font-weight-bold">Template Part Configurations</span>
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="pa-3">
                    <div
                      v-for="(config, idx) in parsedTemplateConfigs"
                      :key="idx"
                      class="mb-3"
                    >
                      <div class="d-flex align-center mb-1">
                        <v-icon small color="primary" class="mr-2">mdi-file-document</v-icon>
                        <strong>{{ getDocTypeName(config.tmpl_id) }}</strong>
                      </div>
                      <div class="ml-6 text-caption">
                        <div><strong>Template Part ID:</strong> {{ config.tp_id }}</div>
                        <div v-if="config.tpa_id"><strong>Attribute ID:</strong> {{ config.tpa_id }}</div>
                      </div>
                      <v-divider v-if="idx < parsedTemplateConfigs.length - 1" class="mt-2"></v-divider>
                    </div>
                  </v-card-text>
                </v-card>
              </v-menu>
            </div>
            <div class="info-item ml-4">
              <span class="info-label">Framework:</span>
              <v-chip color="blue" x-small outlined class="ml-2">
                {{ componentState.levelingConfig.client_framework_config?.framework_value || 'Not configured' }}
              </v-chip>
            </div>
            <div class="info-item ml-4">
              <span class="info-label">Source:</span>
              <v-chip color="grey" x-small outlined class="ml-2">
                {{ componentState.levelingConfig.client_framework_config?.type === 'hr' ? 'Hierarchy' : 'Template Part' }}
              </v-chip>
            </div>
            <div v-if="componentState.levelingConfig.client_framework_config?.track_type" class="info-item ml-4">
              <span class="info-label">Track Source:</span>
              <v-chip color="teal" x-small outlined class="ml-2">
                {{ componentState.levelingConfig.client_framework_config.track_type === 'hr' ? 'Hierarchy' : 'Template Part' }}
              </v-chip>
            </div>
            <v-spacer></v-spacer>
            <v-btn
              small
              outlined
              color="primary"
              @click="editConfiguration"
              :disabled="componentState.savingLeveling"
              class="mr-2"
            >
              <v-icon left small>mdi-pencil</v-icon>
              Edit Config
            </v-btn>
            <v-btn
              small
              outlined
              color="warning"
              @click="resetConfiguration"
              :disabled="componentState.savingLeveling"
            >
              <v-icon left small>mdi-refresh</v-icon>
              Reset
            </v-btn>
          </div>

          <!-- Leveling Matrix -->
          <div v-if="componentState.levelingConfig.client_framework_config?.framework_value === 'RoleMapper'">
            <!-- Node Flow Visualization -->
            <div class="flow-visualization" style="display: flex; gap: 0;">
              <!-- Fixed Left Column - Level Labels -->
              <div class="level-column">
                <!-- Header row to match track labels height -->
                <div class="level-header" style="height: 60px; display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 14px; border-bottom: 2px solid #e0e0e0; color: black;">
                  Level
                </div>
                <!-- Each level row aligned with node centers -->
                <div
                  v-for="level in maxLevel"
                  :key="`level-label-${level}`"
                  class="level-row"
                  :style="{
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderBottom: '1px solid #e0e0e0',
                    gap: '8px',
                    marginTop: level === 1 ? '0px' : '0'
                  }"
                >
                  <div style="display: flex; flex-direction: column; align-items: center; cursor: pointer;" @click="openEditAlias('level', level)">
                    <span style="font-weight: 600; font-size: 13px;">{{ level }}</span>
                    <span v-if="getLevelAliasDisplay(level)" style="font-size: 10px; color: #6b7280; line-height: 1.2;">{{ getLevelAliasDisplay(level) }}</span>
                    <span v-else style="font-size: 9px; color: #9ca3af; font-style: italic; line-height: 1.2;">+ alias</span>
                  </div>
                  <v-menu
                    v-if="getLevelDescriptors(level).length > 0"
                    open-on-hover
                    offset-x
                    max-width="600px"
                    :close-on-content-click="false"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        color="blue"
                        v-bind="attrs"
                        v-on="on"
                        style="cursor: pointer;"
                      >
                        mdi-information
                      </v-icon>
                    </template>
                    <v-card max-width="600px">
                      <v-card-title class="py-2 px-3">
                        <span class="text-subtitle-2 font-weight-bold">Level {{ level }} Descriptors</span>
                      </v-card-title>
                      <v-divider></v-divider>
                      <v-card-text class="pa-3">
                        <div
                          v-for="(desc, idx) in getLevelDescriptors(level)"
                          :key="idx"
                          style="margin-bottom: 12px;"
                        >
                          <v-chip x-small color="primary" class="mb-2">
                            {{ desc.type }}
                          </v-chip>
                          <div style="font-size: 12px; line-height: 1.4; color: #333;">{{ desc.descriptor }}</div>
                          <v-divider v-if="idx < getLevelDescriptors(level).length - 1" class="my-3"></v-divider>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-menu>
                </div>
              </div>

              <!-- SVG Flow Diagram -->
              <svg :key="componentState.renderKey" :width="flowDimensions.width" :height="flowDimensions.height" class="flow-svg">
                <!-- Grid Lines -->
                <g class="grid-layer">
                  <!-- Horizontal lines (for each level) -->
                  <line
                    v-for="level in maxLevel"
                    :key="`h-grid-${level}`"
                    :x1="0"
                    :y1="60 + level * 80"
                    :x2="flowDimensions.width"
                    :y2="60 + level * 80"
                    stroke="#e8e8e8"
                    stroke-width="1"
                  />
                  <!-- Bottom border line for last level -->
                  <line
                    v-if="maxLevel > 0"
                    :key="`h-grid-bottom`"
                    :x1="0"
                    :y1="maxLevel * 80 + 60"
                    :x2="flowDimensions.width"
                    :y2="maxLevel * 80 + 60"
                    stroke="#e8e8e8"
                    stroke-width="1"
                  />
                  <!-- Vertical lines (for each track) -->
                  <line
                    v-for="track in flowTracks"
                    :key="`v-grid-${track.type}`"
                    :x1="track.x - 90"
                    :y1="0"
                    :x2="track.x - 90"
                    :y2="flowDimensions.height"
                    stroke="#e8e8e8"
                    stroke-width="1"
                  />
                </g>

                <!-- Draw nodes first (so they're behind) -->
                <g class="nodes-layer">
                  <g
                    v-for="node in flowNodes"
                    :key="node.key"
                    :transform="`translate(${node.x}, ${node.y})`"
                    class="flow-node"
                    @mouseenter="!node.disabled && (componentState.hoveredCell = node.id)"
                    @mouseleave="componentState.hoveredCell = null"
                    @click="!node.disabled && openCellSelector(node.level, node.type)"
                    :style="{ cursor: node.disabled ? 'not-allowed' : 'pointer', opacity: node.disabled ? 0.3 : 1 }"
                  >
                    <!-- Node background -->
                    <rect
                      :width="node.width"
                      :height="node.height"
                      :x="-node.width/2"
                      :y="-node.height/2"
                      :rx="6"
                      :fill="node.disabled ? '#fafafa' : (node.hasMapping ? '#e8f5e9' : '#f5f5f5')"
                      :stroke="node.disabled ? '#e0e0e0' : (componentState.hoveredCell === node.id ? '#2196f3' : (node.hasFrameworkDataWarning ? '#ff9800' : '#e0e0e0'))"
                      :stroke-width="componentState.hoveredCell === node.id && !node.disabled ? 3 : (node.hasFrameworkDataWarning ? 2 : 1)"
                      :stroke-dasharray="node.disabled ? '5,5' : '0'"
                      class="node-rect"
                    />

                    <!-- Framework data warning indicator -->
                    <g v-if="node.hasFrameworkDataWarning && !node.disabled">
                      <circle cx="55" cy="-25" r="10" fill="#ff9800" opacity="0.9"/>
                      <text x="55" y="-21" text-anchor="middle" font-size="12" fill="white">!</text>
                      <title>Missing {{ node.frameworkDataCompleteness.missingCount }} framework data items</title>
                    </g>

                    <!-- Node label -->
                    <text
                      :y="-10"
                      text-anchor="middle"
                      font-size="11"
                      font-weight="600"
                      :fill="node.disabled ? '#bdbdbd' : (node.hasMapping ? '#2e7d32' : '#757575')"
                    >
                      {{ node.label || `${node.type}${node.level}` }}
                    </text>

                    <!-- Level indicator with inline framework data completion -->
                    <text
                      y="5"
                      text-anchor="middle"
                      font-size="9"
                      :fill="node.disabled ? '#bdbdbd' : '#666'"
                    >
                      Level {{ node.level }}
                      <tspan 
                        v-if="node.hasMapping && !node.disabled && node.frameworkDataCompleteness"
                        :fill="node.frameworkDataCompleteness.isComplete ? '#4caf50' : '#ff9800'"
                      >
                        ({{ node.frameworkDataCompleteness.totalPresent }}/{{ node.frameworkDataCompleteness.totalRequired }})
                      </tspan>
                    </text>

                    <!-- Config button (if has mapping and not disabled) -->
                    <g v-if="node.hasMapping && !node.disabled" @click.stop="openConfigDialog(node.level, node.type)">
                      <circle cx="0" cy="17" r="8" fill="#1976d2" opacity="0.1"/>
                      <text y="22" text-anchor="middle" font-size="14" fill="#1976d2">⚙</text>
                    </g>

                    <!-- Plus icon (if no mapping) -->
                    <g v-else-if="!node.disabled">
                      <text y="29" text-anchor="middle" font-size="16" fill="#bdbdbd">+</text>
                    </g>

                    <!-- Tooltip for framework data details -->
                    <title v-if="node.frameworkDataCompleteness && node.hasMapping">
Framework Data: {{ node.frameworkDataCompleteness.totalPresent }}/{{ node.frameworkDataCompleteness.totalRequired }} complete
{{ node.frameworkDataCompleteness.missingItems.length > 0 ? `Missing: ${node.frameworkDataCompleteness.missingItems.map(item => `${item.part} (${item.type})`).join(', ')}` : 'All framework data complete' }}
                    </title>
                  </g>
                </g>

                <!-- Draw connections on top -->
                <g class="connections-layer">
                  <!-- Connection paths -->
                  <path
                    v-for="connection in flowConnections"
                    :key="connection.key"
                    :d="connection.path"
                    :stroke="connection.stroke"
                    :stroke-width="connection.strokeWidth"
                    :opacity="connection.opacity"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="connection-path"
                  >
                    <title>{{ connection.label }}</title>
                  </path>
                  
                  <!-- Arrowheads -->
                  <g v-for="connection in flowConnections" :key="`arrow-${connection.key}`">
                    <polygon
                      :points="`-8,-4 0,0 -8,4`"
                      :fill="connection.stroke"
                      :opacity="connection.opacity"
                      :transform="`translate(${connection.arrowX}, ${connection.arrowY}) rotate(${connection.arrowAngle})`"
                      class="connection-arrow"
                    >
                      <title>{{ connection.label }}</title>
                    </polygon>
                  </g>
                </g>

                <!-- Track labels -->
                <g v-for="track in flowTracks" :key="`track-${track.type}`" style="cursor: pointer;" @click="openEditAlias('track', track.type)">
                  <text
                    :x="track.x"
                    :y="track.alias ? 22 : 30"
                    text-anchor="middle"
                    font-size="14"
                    font-weight="600"
                    fill="#374151"
                  >
                    {{ track.name }}
                  </text>
                  <text
                    v-if="track.alias"
                    :x="track.x"
                    y="40"
                    text-anchor="middle"
                    font-size="11"
                    fill="#6b7280"
                  >
                    {{ track.alias }}
                  </text>
                  <text
                    v-else
                    :x="track.x"
                    y="46"
                    text-anchor="middle"
                    font-size="10"
                    fill="#9ca3af"
                    font-style="italic"
                  >
                    + alias
                  </text>
                </g>
              </svg>
            </div>
          </div>

          <!-- Custom Framework (if not RoleMapper) -->
          <div v-else-if="componentState.levelingConfig.client_framework_config?.framework_value === 'Custom'" class="custom-framework-container">
            <!-- Empty State -->
            <div v-if="maxLevel === 0 && distinctTypes.length === 0" class="empty-state">
              <v-card flat class="pa-8 text-center">
                <v-icon size="80" color="grey lighten-1">mdi-grid-large</v-icon>
                <h2 class="mt-4 mb-2">Create Your Custom Framework</h2>
                <p class="text-body-1 grey--text mb-6">
                  Get started by adding your first level and type to build your organization's leveling framework.
                </p>
                <div class="d-flex justify-center gap-3">
                  <v-btn
                    color="primary"
                    large
                    @click="addNewLevel"
                  >
                    <v-icon left>mdi-plus</v-icon>
                    Add First Level
                  </v-btn>
                  <v-btn
                    color="primary"
                    large
                    outlined
                    @click="componentState.showAddTypeDialog = true"
                  >
                    <v-icon left>mdi-plus</v-icon>
                    Add First Type
                  </v-btn>
                </div>
                <v-alert type="info" text class="mt-6" dense>
                  <strong>Tip:</strong> Add at least one level and one type to begin mapping your framework.
                </v-alert>
              </v-card>
            </div>

            <!-- Node Flow Visualization for Custom Framework -->
            <div v-else class="flow-visualization" style="display: flex; gap: 0;">
              <!-- Fixed Left Column - Level Labels -->
              <div class="level-column">
                <!-- Header row to match track labels height -->
                <div class="level-header" style="height: 60px; display: flex; align-items: center; justify-content: center; font-weight: 600; font-size:14px; border-bottom: 2px solid #e0e0e0; color: black;">
                  Level
                </div>
                <!-- Show at least one row for add level button if no levels exist -->
                <div
                  v-if="maxLevel === 0 || (componentState.levelingConfig.client_framework_config?.framework_value === 'Custom' && componentState.customLevels.length === 0)"
                  class="level-row level-row-empty"
                  :style="{
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderBottom: '1px solid #e0e0e0',
                    gap: '8px'
                  }"
                >
                  <v-btn
                    small
                    color="primary"
                    @click="addNewLevel"
                    title="Add first level"
                  >
                    <v-icon left small>mdi-plus</v-icon>
                    Add Level
                  </v-btn>
                </div>
                <!-- Each level row aligned with node centers -->
                <div
                  v-for="level in maxLevel"
                  :key="`custom-level-label-${level}`"
                  v-else
                  class="level-row"
                  :style="{
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderBottom: '1px solid #e0e0e0',
                    gap: '8px',
                    marginTop: level === 1 ? '0px' : '0'
                  }"
                >
                  <div style="display: flex; flex-direction: column; align-items: center; cursor: pointer;" @click="openEditAlias('level', level)">
                    <span style="font-weight: 600; font-size: 13px;">{{ level }}</span>
                    <span v-if="getLevelAliasDisplay(level)" style="font-size: 10px; color: #6b7280; line-height: 1.2;">{{ getLevelAliasDisplay(level) }}</span>
                    <span v-else style="font-size: 9px; color: #9ca3af; font-style: italic; line-height: 1.2;">+ alias</span>
                  </div>
                  <!-- Add level button for custom framework -->
                  <v-btn
                    v-if="level === maxLevel"
                    x-small
                    icon
                    color="primary"
                    @click="addNewLevel"
                    title="Add new level"
                  >
                    <v-icon x-small>mdi-plus</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- SVG Flow Diagram for Custom Framework -->
              <svg :key="`custom-${componentState.renderKey}`" :width="flowDimensions.width" :height="flowDimensions.height" class="flow-svg">
                <!-- Grid Lines -->
                <g class="grid-layer">
                  <!-- Horizontal lines (for each level) -->
                  <line
                    v-for="level in maxLevel"
                    :key="`custom-h-grid-${level}`"
                    :x1="0"
                    :y1="60 + level * 80"
                    :x2="flowDimensions.width"
                    :y2="60 + level * 80"
                    stroke="#e8e8e8"
                    stroke-width="1"
                  />
                  <!-- Bottom border line for last level -->
                  <line
                    v-if="maxLevel > 0"
                    :key="`custom-h-grid-bottom`"
                    :x1="0"
                    :y1="maxLevel * 80 + 60"
                    :x2="flowDimensions.width"
                    :y2="maxLevel * 80 + 60"
                    stroke="#e8e8e8"
                    stroke-width="1"
                  />
                  <!-- Vertical lines (for each track) -->
                  <line
                    v-for="track in flowTracks.filter(t => !t.isPlaceholder)"
                    :key="`custom-v-grid-${track.type}`"
                    :x1="track.x - 90"
                    :y1="0"
                    :x2="track.x - 90"
                    :y2="flowDimensions.height"
                    stroke="#e8e8e8"
                    stroke-width="1"
                  />
                </g>

                <!-- Draw nodes first (so they're behind) -->
                <g class="nodes-layer">
                  <g
                    v-for="node in flowNodes"
                    :key="`custom-${node.key}`"
                    :transform="`translate(${node.x}, ${node.y})`"
                    class="flow-node"
                    @mouseenter="componentState.hoveredCell = node.id"
                    @mouseleave="componentState.hoveredCell = null"
                    @click="openCellSelector(node.level, node.type)"
                    :style="{ cursor: 'pointer', opacity: 1 }"
                  >
                    <!-- Node background -->
                    <rect
                      :width="node.width"
                      :height="node.height"
                      :x="-node.width/2"
                      :y="-node.height/2"
                      :rx="6"
                      :fill="node.hasMapping ? '#e8f5e9' : '#f5f5f5'"
                      :stroke="componentState.hoveredCell === node.id ? '#2196f3' : '#e0e0e0'"
                      :stroke-width="componentState.hoveredCell === node.id ? 3 : 1"
                      class="node-rect"
                    />

                    <!-- Node label -->
                    <text
                      :y="-10"
                      text-anchor="middle"
                      font-size="11"
                      font-weight="600"
                      :fill="node.hasMapping ? '#2e7d32' : '#757575'"
                    >
                      {{ node.label || `${node.type}${node.level}` }}
                    </text>

                    <!-- Level indicator -->
                    <text
                      y="5"
                      text-anchor="middle"
                      font-size="9"
                      fill="#666"
                    >
                      Level {{ node.level }}
                    </text>

                    <!-- Config button (if has mapping) -->
                    <g v-if="node.hasMapping" @click.stop="openConfigDialog(node.level, node.type)">
                      <circle cx="0" cy="17" r="8" fill="#1976d2" opacity="0.1"/>
                      <text y="22" text-anchor="middle" font-size="14" fill="#1976d2">⚙</text>
                    </g>

                    <!-- Plus icon (if no mapping) -->
                    <g v-else>
                      <text y="29" text-anchor="middle" font-size="16" fill="#bdbdbd">+</text>
                    </g>
                  </g>
                </g>

                <!-- Draw connections on top -->
                <g class="connections-layer">
                  <!-- Connection paths -->
                  <path
                    v-for="connection in flowConnections"
                    :key="`custom-${connection.key}`"
                    :d="connection.path"
                    :stroke="connection.stroke"
                    :stroke-width="connection.strokeWidth"
                    :opacity="connection.opacity"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="connection-path"
                  >
                    <title>{{ connection.label }}</title>
                  </path>
                  
                  <!-- Arrowheads -->
                  <g v-for="connection in flowConnections" :key="`custom-arrow-${connection.key}`">
                    <polygon
                      :points="`-8,-4 0,0 -8,4`"
                      :fill="connection.stroke"
                      :opacity="connection.opacity"
                      :transform="`translate(${connection.arrowX}, ${connection.arrowY}) rotate(${connection.arrowAngle})`"
                      class="connection-arrow"
                    >
                      <title>{{ connection.label }}</title>
                    </polygon>
                  </g>
                </g>

                <!-- Track labels -->
                <g v-for="track in flowTracks" :key="`custom-track-${track.type}`">
                  <!-- Show add type button for placeholder track (when no types exist) -->
                  <g v-if="track.isPlaceholder">
                    <rect
                      :x="track.x - 60"
                      y="10"
                      width="120"
                      height="40"
                      rx="4"
                      fill="#1976d2"
                      opacity="0.1"
                      @click="componentState.showAddTypeDialog = true"
                      style="cursor: pointer;"
                    />
                    <text
                      :x="track.x"
                      y="35"
                      text-anchor="middle"
                      font-size="14"
                      font-weight="600"
                      fill="#1976d2"
                      style="cursor: pointer;"
                      @click="componentState.showAddTypeDialog = true"
                    >
                      + Add Type
                    </text>
                  </g>
                  <!-- Regular track label with add button for last type -->
                  <g v-else>
                    <g style="cursor: pointer;" @click="openEditAlias('track', track.type)">
                      <text
                        :x="track.x"
                        :y="track.alias ? 22 : 30"
                        text-anchor="middle"
                        font-size="14"
                        font-weight="600"
                        fill="#374151"
                      >
                        {{ track.name }}
                      </text>
                      <text
                        v-if="track.alias"
                        :x="track.x"
                        y="40"
                        text-anchor="middle"
                        font-size="11"
                        fill="#6b7280"
                      >
                        {{ track.alias }}
                      </text>
                      <text
                        v-else
                        :x="track.x"
                        y="46"
                        text-anchor="middle"
                        font-size="10"
                        fill="#9ca3af"
                        font-style="italic"
                      >
                        + alias
                      </text>
                    </g>
                    <!-- Add type button for custom framework (on last track) -->
                    <g v-if="track.type === distinctTypes[distinctTypes.length - 1]">
                      <circle 
                        :cx="track.x + 80" 
                        cy="30" 
                        r="12" 
                        fill="#1976d2" 
                        opacity="0.1"
                        @click="componentState.showAddTypeDialog = true"
                        style="cursor: pointer;"
                      />
                      <text 
                        :x="track.x + 80" 
                        y="35" 
                        text-anchor="middle" 
                        font-size="16" 
                        fill="#1976d2"
                        style="cursor: pointer;"
                        @click="componentState.showAddTypeDialog = true"
                      >
                        +
                      </text>
                    </g>
                  </g>
                </g>
              </svg>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Add Type Dialog -->
    <v-dialog v-model="componentState.showAddTypeDialog" max-width="400px">
      <v-card>
        <v-card-title class="primary white--text">
          <span class="text-h6">Add New Type</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="componentState.showAddTypeDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-text-field
            v-model="componentState.newTypeName"
            label="Type Code"
            outlined
            dense
            hint="Enter a short code for the new type (e.g., 'S', 'IC', 'M')"
            persistent-hint
            :rules="[v => !!v || 'Type code is required', v => !distinctTypes.includes(v) || 'Type already exists']"
            class="mb-3"
          ></v-text-field>
          <v-text-field
            v-model="componentState.newTypeAlias"
            label="Client Alias"
            outlined
            dense
            hint="The client-facing name for this type (e.g., 'Individual Contributor', 'Management')"
            persistent-hint
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="componentState.showAddTypeDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="addNewType" :disabled="!componentState.newTypeName || distinctTypes.includes(componentState.newTypeName)">Add Type</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Configuration Dialog -->
    <v-dialog v-model="componentState.showConfigDialog" max-width="900px" scrollable>
      <v-card>
        <!-- Enhanced Header -->
        <v-card-title class="primary white--text d-flex align-center" style="min-height: 64px;">
          <v-icon color="white" class="mr-3">mdi-tune-variant</v-icon>
          <div>
            <span class="text-h6">Configure Mapping</span>
            <div class="text-caption" style="opacity:0.85;">Set up mapping details for this level and type</div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="componentState.showConfigDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4 pb-0" style="background: #f7fafd;">
          <div v-if="componentState.editingMapping">
            <!-- Mapping Summary -->
            <div class="mb-4 d-flex align-center gap-2">
              <v-chip color="primary" dark label class="ml-2">
                Level {{ componentState.editingMapping.level }}
              </v-chip>
              <v-chip color="blue" label outlined class="ml-2">
                {{ componentState.editingMapping.type }}
              </v-chip>
              <v-chip color="grey" label outlined class="ml-2">
                <v-icon left small>mdi-account-circle</v-icon>
                {{ componentState.editingMapping.client_value || 'No value set' }}
              </v-chip>
            </div>

            <v-form>
              <!-- Section: Mapping Details -->
              <v-card flat class="mb-4" style="background: #fff; border-radius: 8px;">
                <v-card-title class="py-2 px-3" style="font-size: 16px; font-weight: 500; color: #1976d2;">
                  <v-icon left color="primary" small>mdi-link-variant</v-icon>
                  Mapping Details
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4 pb-2">
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <label class="font-weight-bold mb-1" style="font-size:13px;">Client Value</label>
                      <v-select
                        v-if="componentState.levelingConfig.client_framework_config?.type === 'hr'"
                        v-model="componentState.editingMapping.client_value"
                        :items="hierarchyValues"
                        item-text="text"
                        item-value="text"
                        label="Select Hierarchy Value"
                        outlined
                        dense
                        hint="Select the hierarchy value that represents this level"
                        persistent-hint
                        :rules="[v => !!v || 'Client value is required']"
                      ></v-select>
                      <v-text-field
                        v-else
                        v-model="componentState.editingMapping.client_value"
                        label="Client Value"
                        outlined
                        dense
                        hint="The value that represents this level in your organization"
                        persistent-hint
                        :rules="[v => !!v || 'Client value is required']"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <label class="font-weight-bold mb-1" style="font-size:13px;">Progression Path (Next Levels)</label>
                      <v-combobox
                        v-model="componentState.editingMapping.progression_next"
                        :items="getProgressionOptions()"
                        multiple
                        chips
                        deletable-chips
                        outlined
                        dense
                        label="Next levels in progression"
                        hint="Select or type level-type combinations (e.g., 2-IC, 3-S)"
                        persistent-hint
                      ></v-combobox>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Section: Description -->
              <v-card flat class="mb-4" style="background: #fff; border-radius: 8px;">
                <v-card-title class="py-2 px-3" style="font-size: 16px; font-weight: 500; color: #1976d2;">
                  <v-icon left color="primary" small>mdi-text-long</v-icon>
                  Description
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4 pb-2">
                  <v-textarea
                    v-model="componentState.editingMapping.descriptor"
                    label="Description"
                    outlined
                    dense
                    rows="3"
                    hint="Optional description for this mapping"
                    persistent-hint
                    style="background: #f8fafd; border-radius: 6px;"
                  ></v-textarea>
                </v-card-text>
              </v-card>

              <!-- Section: Framework Data -->
              <v-card flat class="mb-2" style="background: #fff; border-radius: 8px;">
                <v-card-title class="py-2 px-3" style="font-size: 16px; font-weight: 500; color: #1976d2;">
                  <v-icon left color="primary" small>mdi-database-outline</v-icon>
                  Framework Data
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4 pb-2">
                  <div class="mb-2 text-caption" style="color: #666;">
                    <span v-if="componentState.levelingConfig.client_framework_config?.framework_value === 'RoleMapper'">
                      Override the default framework data for this specific mapping. Leave empty to use default framework data.
                    </span>
                    <span v-else>
                      Define custom framework data for this level and type combination. This data can be used by AI content generation and other features.
                    </span>
                  </div>
                  <div class="framework-data-container" style="background: #f8fafd; border-radius: 8px; border: 1px solid #e9ecef;">
                    <!-- Data Items List -->
                    <div 
                      v-if="componentState.editingMapping.framework_data.length > 0"
                      class="framework-data-items-container"
                    >
                      <div class="framework-data-items-header" style="background: #f4f7fb; padding: 12px 16px; border-bottom: 1px solid #e9ecef; display: flex; justify-content: space-between; align-items: center;">
                        <span class="items-count" style="font-size: 13px; font-weight: 500; color: #495057;">
                          {{ componentState.editingMapping.framework_data.length }} item{{ componentState.editingMapping.framework_data.length > 1 ? 's' : '' }}
                        </span>
                        <v-btn
                          small
                          outlined
                          color="error"
                          @click="componentState.editingMapping.framework_data = []"
                          :disabled="componentState.editingMapping.framework_data.length === 0"
                        >
                          <v-icon small left>mdi-delete-sweep</v-icon>
                          Clear All
                        </v-btn>
                      </div>
                      <div class="framework-data-items-list" style="max-height: 300px; overflow-y: auto; background: #fff;">
                        <div
                          v-for="(data, index) in componentState.editingMapping.framework_data"
                          :key="index"
                          class="framework-data-item-row"
                          style="border-bottom: 1px solid #f1f3f4;"
                        >
                          <div class="framework-data-item-content" style="padding: 16px;">
                            <div class="framework-data-item-header" style="display: flex; align-items: flex-start; gap: 16px;">
                              <div class="item-index" style="background: #e9ecef; color: #495057; font-weight: 600; font-size: 12px; padding: 6px 10px; border-radius: 4px; min-width: 32px; text-align: center; margin-top: 20px; flex-shrink: 0;">
                                {{ index + 1 }}
                              </div>
                              <div class="item-fields" style="display: flex; gap: 12px; flex: 1; align-items: flex-end; flex-wrap: wrap;">
                                <div class="field-group" style="display: flex; flex-direction: column; min-width: 120px; flex: 1;">
                                  <label class="field-label" style="font-size: 11px; font-weight: 500; color: #6c757d; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.5px;">
                                    Part
                                  </label>
                                  <v-text-field
                                    v-model="data.part"
                                    dense
                                    outlined
                                    hide-details
                                    placeholder="Enter part name"
                                    class="field-input"
                                  ></v-text-field>
                                </div>
                                <div class="field-group" style="display: flex; flex-direction: column; min-width: 120px; flex: 1;">
                                  <label class="field-label" style="font-size: 11px; font-weight: 500; color: #6c757d; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.5px;">
                                    Type
                                  </label>
                                  <v-text-field
                                    v-model="data.type"
                                    dense
                                    outlined
                                    hide-details
                                    placeholder="Enter type"
                                    class="field-input"
                                  ></v-text-field>
                                </div>
                                <div class="field-group field-group-wide" style="display: flex; flex-direction: column; flex: 2; min-width: 200px;">
                                  <label class="field-label" style="font-size: 11px; font-weight: 500; color: #6c757d; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.5px;">
                                    Value
                                  </label>
                                  <v-text-field
                                    v-model="data.value"
                                    dense
                                    outlined
                                    hide-details
                                    placeholder="Enter value"
                                    class="field-input"
                                  ></v-text-field>
                                </div>
                              </div>
                              <div class="item-actions" style="margin-top: 20px; flex-shrink: 0;">
                                <v-btn
                                  icon
                                  small
                                  color="error"
                                  @click="removeFrameworkDataItem(index)"
                                  class="delete-btn"
                                >
                                  <v-icon small>mdi-delete</v-icon>
                                </v-btn>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- Empty State -->
                    <div v-else class="framework-data-empty-state" style="text-align: center; padding: 40px 20px; background: #fff;">
                      <v-icon size="48" color="grey lighten-1" class="mb-3">mdi-database-outline</v-icon>
                      <p class="empty-text" style="font-size: 14px; font-weight: 500; color: #6c757d; margin: 0 0 4px 0;">
                        No framework data items configured
                      </p>
                      <p class="empty-subtext" style="font-size: 12px; color: #adb5bd; margin: 0;">
                        <span v-if="componentState.levelingConfig.client_framework_config?.framework_value === 'RoleMapper'">
                          Add custom framework data or leave empty to use defaults
                        </span>
                        <span v-else>
                          Add framework data items to define custom data for this level and type
                        </span>
                      </p>
                    </div>
                    <!-- Add Button -->
                    <div class="framework-data-actions" style="padding: 16px; text-align: center; background: #fff; border-top: 1px solid #e9ecef;">
                      <v-btn
                        color="primary"
                        outlined
                        @click="addFrameworkDataItem"
                        class="add-data-btn"
                      >
                        <v-icon left small>mdi-plus</v-icon>
                        Add Framework Data Item
                      </v-btn>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-form>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="px-6 py-4" style="background: #f7fafd;">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-2" @click="componentState.showConfigDialog = false" style="font-weight:500;">
            Cancel
          </v-btn>
          <v-btn 
            color="primary" 
            @click="saveConfiguration"
            :disabled="!componentState.editingMapping?.client_value"
            style="font-weight:600; min-width:140px;"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save Configuration
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Alias Dialog -->
    <v-dialog v-model="componentState.showEditAliasDialog" max-width="400px">
      <v-card>
        <v-card-title class="primary white--text">
          <span class="text-h6">{{ componentState.editAlias.mode === 'track' ? 'Edit Track' : 'Edit Level' }}</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="componentState.showEditAliasDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="mb-3">
            <v-chip color="primary" small label>
              {{ componentState.editAlias.mode === 'track' ? 'Track' : 'Level' }}: {{ componentState.editAlias.key }}
            </v-chip>
          </div>
          <v-text-field
            v-model="componentState.editAlias.alias"
            label="Client Alias"
            outlined
            dense
            :hint="componentState.editAlias.mode === 'track' ? 'Client-facing name for this track (e.g., \'Individual Contributor\')' : 'Client-facing name for this level (e.g., \'Junior\', \'Senior\')'"
            persistent-hint
            clearable
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="componentState.showEditAliasDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="saveAlias">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="componentState.showSnack" :timeout="componentState.snackTimeout" :color="componentState.snackColor" top>
      {{ componentState.snackText }}
      <template v-slot:action="{ attrs }">
        <v-btn color="blue" text v-bind="attrs" @click="componentState.showSnack = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup>
//#region Imports
import { ref, onBeforeMount, onUpdated, watch, reactive, getCurrentInstance, computed, nextTick  } from 'vue';
import axios from 'axios';
import { inject } from 'vue';
//#endregion

//#region Reactive
const store = inject('store');

const componentState = reactive({
    levelingConfig: {
        client_framework_config: null,
        client_framework_mapping: [],
        full_framework: null
    },
    customLevels: [],
    customTypes: [],
    showConfigDialog: false,
    showAddTypeDialog: false,
    newTypeName: "",
    newTypeAlias: "",
    editingMapping: {
        level: null,
        type: null,
        value: null,
        descriptor: "",
        framework_data: []
    },
    hoveredCell: null, // Fix: ensure this is defined
    showEditAliasDialog: false,
    editAlias: {
        mode: null,  // 'track' or 'level'
        key: null,   // the type code or level number
        alias: null
    },
    renderKey: 0,
    savingLeveling: false,
    showSnack: false,
    snackText: "",
    snackColor: "success",
    snackTimeout: 4000,
    isLoading: false,

    // New configuration state
    configFormValid: false,
    savingConfig: false,
    isEditMode: false,
    newConfig: {
        framework_value: null,
        type: null,
        ht_id: null,
        ht_level: null,
        template_configs: [
            {
                tp_id: null,
                tpa_id: null,
                tmpl_id: null,
                loadingParts: false,
                templateParts: []
            }
        ],
        track_enabled: false,
        track_type: null,
        track_ht_id: null,
        track_ht_level: null,
        track_template_configs: [
            {
                tp_id: null,
                tpa_id: null,
                tmpl_id: null,
                loadingParts: false,
                templateParts: []
            }
        ]
    }
});
//#endregion

//#region Computed

const hierarchies = computed(() => store.state.hierarchies.hierarchies)

const hierarchyValues = computed(() => {
    if (!componentState.levelingConfig.client_framework_config?.ht_id) return [];
    const hierarchy = hierarchies.value.find(h => h.ht_id === componentState.levelingConfig.client_framework_config.ht_id);
    if (!hierarchy || !hierarchy.values) return [];
    return hierarchy.values.map(v => ({
        text: v.text,
        value: v.value
    }));
})

const unmappedHierarchyCount = computed(() => {
    if (componentState.levelingConfig.client_framework_config?.type !== 'hr') return 0;
    const ht_id = componentState.levelingConfig.client_framework_config?.ht_id;
    if (!ht_id) return 0;
    const hierarchy = hierarchies.value.find(h => h.ht_id === ht_id);
    if (!hierarchy || !hierarchy.values) return 0;
    const mappedValues = componentState.levelingConfig.client_framework_mapping
        .map(m => m.client_value)
        .filter(v => !!v);
    return hierarchy.values.filter(v => !mappedValues.includes(v.text)).length;
})

const unmappedHierarchyValues = computed(() => {
    if (componentState.levelingConfig.client_framework_config?.type !== 'hr') return [];
    const ht_id = componentState.levelingConfig.client_framework_config?.ht_id;
    if (!ht_id) return [];
    const hierarchy = hierarchies.value.find(h => h.ht_id === ht_id);
    if (!hierarchy || !hierarchy.values) return [];
    const mappedValues = componentState.levelingConfig.client_framework_mapping
        .map(m => m.client_value)
        .filter(v => !!v);
    return hierarchy.values.filter(v => !mappedValues.includes(v.text)).map(v => v.text);
})

const distinctTypes = computed(() => {
    // For Custom framework, use customTypes if available
    if (componentState.levelingConfig.client_framework_config?.framework_value === 'Custom') {
        return [...componentState.customTypes].sort();
    }

    // Use full_framework to determine columns if available
    if (componentState.levelingConfig.full_framework && Array.isArray(componentState.levelingConfig.full_framework)) {
        const types = [...new Set(componentState.levelingConfig.full_framework.map(f => f.type))];
        return types.filter(t => t).sort();
    }

    // Get types from client_framework_mapping (new format)
    if (componentState.levelingConfig.client_framework_mapping && componentState.levelingConfig.client_framework_mapping.length > 0) {
        const types = [...new Set(componentState.levelingConfig.client_framework_mapping.map(m => m.type))];
        return types.filter(t => t).sort();
    }

    return [];
})

const maxLevel = computed(() => {
    // For Custom framework, use customLevels if available
    if (componentState.levelingConfig.client_framework_config?.framework_value === 'Custom') {
        return componentState.customLevels.length > 0 ? Math.max(...componentState.customLevels) : 1;
    }

    // Use full_framework to determine max level if available
    if (componentState.levelingConfig.full_framework && Array.isArray(componentState.levelingConfig.full_framework) && componentState.levelingConfig.full_framework.length > 0) {
        const levels = componentState.levelingConfig.full_framework.map(f => parseInt(f.level));
        return Math.max(...levels);
    }

    // Get max level from client_framework_mapping (new format)
    if (componentState.levelingConfig.client_framework_mapping && componentState.levelingConfig.client_framework_mapping.length > 0) {
        const levels = componentState.levelingConfig.client_framework_mapping.map(m => parseInt(m.level));
        return Math.max(...levels);
    }

    // For Custom framework with no levels, return 1 to show add button
    if (componentState.levelingConfig.client_framework_config?.framework_value === 'Custom') {
        return 1;
    }

    return 0;
})

const flowTracks = computed(() => {
    const types = distinctTypes.value;
    
    // For Custom framework with no types, return a placeholder track to show add button
    if (componentState.levelingConfig.client_framework_config?.framework_value === 'Custom' && types.length === 0) {
        const nodeWidth = 140;
        const columnPadding = 60;
        const leftMargin = 100;
        
        return [{
            type: '_placeholder',
            name: '',
            x: leftMargin,
            isPlaceholder: true
        }];
    }
    
    const nodeWidth = 140;
    const columnPadding = 60;
    const leftMargin = 100;
    const columnSpacing = nodeWidth + columnPadding;

    return types.map((type, index) => {
        const mapping = componentState.levelingConfig.client_framework_mapping?.find(m => m.type === type);
        return {
            type: type,
            name: type,
            alias: mapping?.type_alias || null,
            x: leftMargin + columnSpacing * index,
            isPlaceholder: false
        };
    });
})

const flowDimensions = computed(() => {
    const types = distinctTypes.value.length || 1; // At least 1 for placeholder
    const levels = maxLevel.value || 1; // At least 1
    const nodeWidth = 140;
    const columnPadding = 60;
    const leftMargin = 100;
    const rightMargin = 100;
    const columnSpacing = nodeWidth + columnPadding;

    return {
        width: leftMargin + (types * columnSpacing) + rightMargin,
        height: Math.max(600, levels * 90 + 100)
    };
})

const flowNodes = computed(() => {
    const nodes = [];
    const types = distinctTypes.value;
    const levels = maxLevel.value;
    
    // Don't generate nodes if there are no types (placeholder scenario)
    if (types.length === 0) {
        return nodes;
    }
    
    const nodeWidth = 140;
    const columnPadding = 60;
    const leftMargin = 100;
    const columnSpacing = nodeWidth + columnPadding;

    for (let level = 1; level <= levels; level++) {
        types.forEach((type, typeIndex) => {
            const id = `${level}-${type}`;
            
            // For RoleMapper framework, check if this level/type combination exists in full_framework
            let existsInFramework = true;
            let frameworkDescriptor = null;
            if (componentState.levelingConfig.client_framework_config?.framework_value === 'RoleMapper' && 
                componentState.levelingConfig.full_framework && 
                Array.isArray(componentState.levelingConfig.full_framework)) {
                const frameworkItem = componentState.levelingConfig.full_framework.find(f => 
                    parseInt(f.level) === level && f.type === type
                );
                existsInFramework = !!frameworkItem;
                frameworkDescriptor = frameworkItem?.descriptor || null;
            }
            
            const mapping = componentState.levelingConfig.client_framework_mapping.find(m => {
                return `${m.level}-${m.type}` === id;
            });

            nodes.push({
                key: id,
                id: id,
                level: level,
                type: type,
                x: leftMargin + columnSpacing * typeIndex,
                y: 20 + level * 80,
                width: nodeWidth,
                height: 60,
                hasMapping: !!mapping && !!mapping.client_value,
                label: mapping?.client_value || null,
                disabled: !existsInFramework,
                frameworkDescriptor: frameworkDescriptor,
                hasFrameworkDataWarning: false
            });
        });
    }
    return nodes;
})

const flowConnections = computed(() => {
    const connections = [];

    if (!componentState.levelingConfig.client_framework_mapping || !componentState.hoveredCell) return connections;

    componentState.levelingConfig.client_framework_mapping.forEach(mapping => {
        const fromValue = `${mapping.level}-${mapping.type}`;
        const nextValues = Array.isArray(mapping.progression_next) ? mapping.progression_next : (mapping.progression_next ? [mapping.progression_next] : []);

        // Only show connections for the hovered cell
        if (componentState.hoveredCell !== fromValue && !nextValues.includes(componentState.hoveredCell)) {
            return;
        }

        nextValues.forEach(nextValue => {
            if (nextValue) {
                const fromNode = flowNodes.value.find(n => n.id === fromValue);
                const toNode = flowNodes.value.find(n => n.id === nextValue);

                if (fromNode && toNode) {
                    const deltaX = toNode.x - fromNode.x;
                    const deltaY = toNode.y - fromNode.y;
                    
                    // Determine if progression is upward or downward based on level
                    const isUpwardProgression = toNode.level < fromNode.level;
                    
                    let startX, startY, endX, endY, path, arrowX, arrowY, angle;
                    
                    // Check if it's a vertical connection (same column)
                    if (Math.abs(deltaX) < 10) {
                        // Vertical connection
                        if (isUpwardProgression) {
                          // Upward: draw from top of fromNode to bottom of toNode
                          startX = fromNode.x;
                          startY = fromNode.y - fromNode.height/2;
                          endX = toNode.x;
                          endY = toNode.y + toNode.height/2;
                        } else {
                          // Downward: draw from bottom of fromNode to top of toNode
                          startX = fromNode.x;
                          startY = fromNode.y + fromNode.height/2;
                          endX = toNode.x;
                          endY = toNode.y - toNode.height/2;
                        }
                        
                        // Simple straight line for vertical connections
                        path = `M ${startX} ${startY} L ${endX} ${endY}`;
                        
                        // Arrow position and angle for vertical
                        arrowX = endX;
                        arrowY = endY;
                        angle = deltaY > 0 ? 90 : -90; // Straight down or up
                    } else {
                        // Horizontal or diagonal connection - use bezier curves
                        // Determine connection points based on progression direction
                        if (isUpwardProgression) {
                          // Upward progression: connect from top-side of fromNode to bottom-side of toNode
                          startX = fromNode.x + (deltaX > 0 ? fromNode.width/2 : -fromNode.width/2);
                          startY = fromNode.y - fromNode.height/4; // Upper portion of node
                          endX = toNode.x + (deltaX > 0 ? -toNode.width/2 : toNode.width/2);
                          endY = toNode.y + toNode.height/4; // Lower portion of node
                        } else {
                          // Downward progression: connect from bottom-side of fromNode to top-side of toNode
                          startX = fromNode.x + (deltaX > 0 ? fromNode.width/2 : -fromNode.width/2);
                          startY = fromNode.y + fromNode.height/4; // Lower portion of node
                          endX = toNode.x + (deltaX > 0 ? -toNode.width/2 : toNode.width/2);
                          endY = toNode.y - toNode.height/4; // Upper portion of node
                        }
                        
                        // Create control points for smooth bezier curve
                        const controlX1 = startX + deltaX * 0.3;
                        const controlY1 = startY;
                        const controlX2 = endX - deltaX * 0.3;
                        const controlY2 = endY;
                        
                        path = `M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`;
                        
                        // Arrow position and angle for horizontal connections
                        arrowX = endX;
                        arrowY = endY;
                        // Calculate angle based on the final direction (from controlX2 to endX)
                        const finalDeltaX = endX - controlX2;
                        const finalDeltaY = endY - controlY2;
                        angle = Math.atan2(finalDeltaY, finalDeltaX) * 180 / Math.PI;
                    }
                    
                    connections.push({
                        key: `${fromValue}-to-${nextValue}`,
                        isHighlighted: true,
                        label: `${fromNode.label || fromValue} → ${toNode.label || nextValue}`,
                        path: path,
                        arrowX: arrowX,
                        arrowY: arrowY,
                        arrowAngle: angle,
                        stroke: '#1976d2',
                        strokeWidth: 3,
                        opacity: 0.9
                    });
                }
            }
        });
    });
    return connections;
})

const hierarchyOptions = computed(() => {
    return store.state.hierarchies?.hierarchies || [];
})

const selectedHierarchyLevels = computed(() => {
    if (!componentState.newConfig.ht_id) return [];

    const hierarchy = hierarchyOptions.value.find(h => h.ht_id === componentState.newConfig.ht_id);
    if (!hierarchy) return [];

    const levels = [];

    if (hierarchy.levels) {
        hierarchy.levels.forEach(l => {
            levels.push({
                name: l.name,
                index: l.index
            });
        });
    }

    return levels;
})

const selectedTrackHierarchyLevels = computed(() => {
    if (!componentState.newConfig.track_ht_id) return [];

    const hierarchy = hierarchyOptions.value.find(h => h.ht_id === componentState.newConfig.track_ht_id);
    if (!hierarchy) return [];

    const levels = [];

    if (hierarchy.levels) {
        hierarchy.levels.forEach(l => {
            levels.push({
                name: l.name,
                index: l.index
            });
        });
    }

    return levels;
})

const isConfigValid = computed(() => {
    if (!componentState.newConfig.framework_value || !componentState.newConfig.type) {
        return false;
    }

    let levelValid = false;
    if (componentState.newConfig.type === 'hr') {
        levelValid = !!componentState.newConfig.ht_id && !!componentState.newConfig.ht_level;
    } else if (componentState.newConfig.type === 'tp_id') {
        levelValid = componentState.newConfig.template_configs.every(config =>
            config.tp_id && config.tmpl_id
        );
    }

    if (!levelValid) return false;

    if (componentState.newConfig.track_enabled) {
        if (!componentState.newConfig.track_type) return false;

        if (componentState.newConfig.track_type === 'hr') {
            if (!componentState.newConfig.track_ht_id || !componentState.newConfig.track_ht_level) return false;
        } else if (componentState.newConfig.track_type === 'tp_id') {
            if (!componentState.newConfig.track_template_configs.every(config =>
                config.tp_id && config.tmpl_id
            )) return false;
        }
    }

    return true;
})

const docTypeOptions = computed(() => {
    return store.state.hierarchies?.docTypes || [];
})

const parsedTemplateConfigs = computed(() => {
    if (!componentState.levelingConfig.client_framework_config?.template_configs) return [];

    const configs = componentState.levelingConfig.client_framework_config.template_configs;
    if (typeof configs === 'string') {
        try {
            return JSON.parse(configs);
        } catch (error) {
            console.error('Error parsing template_configs:', error);
            return [];
        }
    }
    return Array.isArray(configs) ? configs : [];
})

const templateConfigCount = computed(() => {
    return parsedTemplateConfigs.value.length;
})

const parsedTrackTemplateConfigs = computed(() => {
    if (!componentState.levelingConfig.client_framework_config?.track_template_configs) return [];

    const configs = componentState.levelingConfig.client_framework_config.track_template_configs;
    if (typeof configs === 'string') {
        try {
            return JSON.parse(configs);
        } catch (error) {
            console.error('Error parsing track_template_configs:', error);
            return [];
        }
    }
    return Array.isArray(configs) ? configs : [];
})

const isEditMode = computed(() => {
    return componentState.isEditMode;
})

const distinctFrameworkDataTemplates = computed(() => {
    const templates = new Map();

    componentState.levelingConfig.client_framework_mapping?.forEach(mapping => {
        if (mapping.framework_data && Array.isArray(mapping.framework_data)) {
            mapping.framework_data.forEach(item => {
                if (item.part && item.type) {
                    const key = `${item.part}|${item.type}`;
                    if (!templates.has(key)) {
                        templates.set(key, {
                            part: item.part,
                            type: item.type,
                            value: ''
                        });
                    }
                }
            });
        }
    });

    return Array.from(templates.values());
})

//#endregion

//#region Methods

function triggerNotification(text, type) {
    componentState.snackText = text;
    componentState.snackColor = type;
    componentState.showSnack = true;
}

function getHierarchyName(ht_id) {
    const hierarchy = hierarchies.value.find(h => h.ht_id === ht_id);
    return hierarchy ? hierarchy.ht_name : 'Unknown';
}

function getLevelAliasDisplay(level) {
    const mapping = componentState.levelingConfig.client_framework_mapping?.find(m => String(m.level) === String(level));
    return mapping?.level_alias || null;
}

function getDocTypeName(tmpl_id) {
    const docType = docTypeOptions.value.find(dt => dt.tmpl_id === tmpl_id);
    return docType ? docType.tmpl_name : `Template ${tmpl_id}`;
}

async function loadLevellingConfig() {
    componentState.isLoading = true;
    
    try {
        const response = await axios.get('admin/levellingConfig');
        
        if (response.data && response.data.Status === 'OK') {
            componentState.levelingConfig = response.data.Data || {
                client_framework_config: null,
                client_framework_mapping: [],
                full_framework: null
            };

            // Parse framework_config JSON if it exists (database column name)
            if (componentState.levelingConfig.client_framework_config?.framework_config) {
                try {
                    if (typeof componentState.levelingConfig.client_framework_config.framework_config === 'string') {
                        componentState.levelingConfig.client_framework_config.template_configs =
                            JSON.parse(componentState.levelingConfig.client_framework_config.framework_config);
                    } else {
                        componentState.levelingConfig.client_framework_config.template_configs =
                            componentState.levelingConfig.client_framework_config.framework_config;
                    }
                } catch (error) {
                    console.error('Error parsing framework_config:', error);
                }
            }

            // Parse track_config JSON if it exists (database column name)
            if (componentState.levelingConfig.client_framework_config?.track_config) {
                try {
                    if (typeof componentState.levelingConfig.client_framework_config.track_config === 'string') {
                        componentState.levelingConfig.client_framework_config.track_template_configs =
                            JSON.parse(componentState.levelingConfig.client_framework_config.track_config);
                    } else {
                        componentState.levelingConfig.client_framework_config.track_template_configs =
                            componentState.levelingConfig.client_framework_config.track_config;
                    }
                } catch (error) {
                    console.error('Error parsing track_config:', error);
                }
            }

            if (componentState.levelingConfig.client_framework_config?.framework_value === 'Custom' &&
                componentState.levelingConfig.client_framework_mapping &&
                componentState.levelingConfig.client_framework_mapping.length > 0) {
                const levels = [...new Set(componentState.levelingConfig.client_framework_mapping.map(m => parseInt(m.level)))];
                componentState.customLevels = levels.sort((a, b) => a - b);

                const types = [...new Set(componentState.levelingConfig.client_framework_mapping.map(m => m.type))];
                componentState.customTypes = types.filter(t => t).sort();
            }
        } else {
            triggerNotification(response.data?.Message || 'Failed to load levelling configuration', 'error');
        }
    } catch (error) {
        console.error('Error loading levelling configuration:', error);
        triggerNotification('Error loading levelling configuration', 'error');
    } finally {
        componentState.isLoading = false;
    }
}

function getLevelDescriptors(level) {
    // For RoleMapper framework, get descriptors from full_framework (preferred source)
    if (componentState.levelingConfig.client_framework_config?.framework_value === 'RoleMapper' && 
        componentState.levelingConfig.full_framework && 
        Array.isArray(componentState.levelingConfig.full_framework)) {
        const frameworkItems = componentState.levelingConfig.full_framework.filter(f => parseInt(f.level) === level);
        if (frameworkItems.length > 0) {
            return frameworkItems
                .filter(f => f.descriptor && f.descriptor.trim())
                .map(f => ({
                    type: f.type,
                    descriptor: f.descriptor
                }));
        }
    }

    // Fallback to client_framework_mapping
    if (!componentState.levelingConfig.client_framework_mapping) return [];

    const mappings = componentState.levelingConfig.client_framework_mapping.filter(m => {
        return parseInt(m.level) === level;
    });

    return mappings
        .filter(m => m.descriptor && m.descriptor.trim())
        .map(m => ({
            type: m.type || 'Unknown',
            descriptor: m.descriptor
        }));
}

function openCellSelector(level, type) {
    // Check if mapping exists
    const existing = componentState.levelingConfig.client_framework_mapping.find(m => {
        return `${m.level}-${m.type}` === `${level}-${type}`;
    });

    if (existing) {
        openConfigDialog(level, type);
    } else {
        // Inherit aliases from existing mappings with same type/level
        const sameType = componentState.levelingConfig.client_framework_mapping.find(m => m.type === type);
        const sameLevel = componentState.levelingConfig.client_framework_mapping.find(m => String(m.level) === String(level));
        const newMapping = {
            client_level_framework_map_id: null,
            client_value: null,
            level: String(level),
            level_alias: sameLevel?.level_alias || null,
            type: type,
            type_alias: sameType?.type_alias || null,
            descriptor: '',
            level_framework_value: null,
            progression_previous: null,
            progression_next: [],
            framework_data: []
        };

        componentState.levelingConfig.client_framework_mapping.push(newMapping);
        openConfigDialog(level, type);
    }
}

function getTemplatedFrameworkData(existingData, level, type) {
    let frameworkData = existingData || [];

    if (distinctFrameworkDataTemplates.value.length > 0) {
        const existingDataMap = new Map();
        frameworkData.forEach(item => {
            if (item.part && item.type) {
                existingDataMap.set(`${item.part}|${item.type}`, item.value || '');
            }
        });

        frameworkData = distinctFrameworkDataTemplates.value.map(template => ({
            part: template.part,
            type: template.type,
            value: existingDataMap.get(`${template.part}|${template.type}`) || ''
        }));
    }

    if (
        frameworkData.length === 0 &&
        componentState.levelingConfig.client_framework_config?.framework_value === 'RoleMapper' &&
        componentState.levelingConfig.base_framework_data
    ) {
        const baseData = componentState.levelingConfig.base_framework_data.find(item =>
            parseInt(item.level) === level && item.type === type
        );

        if (baseData && baseData.items) {
            frameworkData = baseData.items.map(item => ({
                part: item.part || '',
                type: item.type || '',
                value: item.value || ''
            }));
        }
    }

    return frameworkData;
}

function openConfigDialog(level, type) {
    const mapping = componentState.levelingConfig.client_framework_mapping.find(m => {
        return `${m.level}-${m.type}` === `${level}-${type}`;
    });

    if (!mapping) {
        triggerNotification('No mapping found for this cell', 'error');
        return;
    }

    let frameworkData = getTemplatedFrameworkData(mapping.framework_data, level, type);
    let descriptor = mapping.descriptor || '';

    if (
        !descriptor &&
        componentState.levelingConfig.client_framework_config?.framework_value === 'RoleMapper' &&
        componentState.levelingConfig.base_framework_data
    ) {
        const baseData = componentState.levelingConfig.base_framework_data.find(item =>
            parseInt(item.level) === level && item.type === type
        );

        if (baseData && baseData.descriptor) {
            descriptor = baseData.descriptor;
        }
    }

    componentState.editingMapping = JSON.parse(JSON.stringify({
        ...mapping,
        level: String(level),
        type: type,
        framework_data: frameworkData,
        descriptor: descriptor,
        progression_next: Array.isArray(mapping.progression_next) ? mapping.progression_next : (mapping.progression_next ? [mapping.progression_next] : [])
    }));
    componentState.showConfigDialog = true;
}

function saveConfiguration() {
    if (!componentState.editingMapping.client_value || !componentState.editingMapping.client_value.trim()) {
        triggerNotification('Please provide a client value', 'error');
        return;
    }

    const index = componentState.levelingConfig.client_framework_mapping.findIndex(m => {
        return `${m.level}-${m.type}` === `${componentState.editingMapping.level}-${componentState.editingMapping.type}`;
    });
    
    if (index !== -1) {
        // Create a completely new object to ensure reactivity
        const updatedMapping = {
            ...componentState.editingMapping,
            // Extract just the values from progression_next, handling both string values and objects
            progression_next: Array.isArray(componentState.editingMapping.progression_next) 
                ? componentState.editingMapping.progression_next.map(item => 
                    typeof item === 'object' && item.value ? item.value : item
                  ).filter(item => item) // Remove any null/undefined values
                : []
        };
        
        // Replace the mapping with a new object reference
        componentState.levelingConfig.client_framework_mapping.splice(index, 1, updatedMapping);
        
        // Force reactivity update
        componentState.renderKey++;
        
        triggerNotification('Configuration saved', 'success');
    } else {
        triggerNotification('Mapping not found', 'error');
    }

    componentState.showConfigDialog = false;
}

function addNewLevel() {
    const currentMax = maxLevel.value;
    const newLevel = currentMax + 1;

    if (componentState.levelingConfig.client_framework_config?.framework_value === 'Custom') {
        if (!componentState.customLevels.includes(newLevel)) {
            componentState.customLevels.push(newLevel);
            componentState.customLevels.sort((a, b) => a - b);
        }

        const types = componentState.customTypes;
        if (types.length > 0) {
            types.forEach(type => {
                const exists = componentState.levelingConfig.client_framework_mapping.some(m =>
                    `${m.level}-${m.type}` === `${newLevel}-${type}`
                );

                if (!exists) {
                    // Inherit aliases from existing mappings
                    const sameType = componentState.levelingConfig.client_framework_mapping.find(m => m.type === type);
                    componentState.levelingConfig.client_framework_mapping.push({
                        client_level_framework_map_id: null,
                        client_value: null,
                        level: String(newLevel),
                        level_alias: null,
                        type: type,
                        type_alias: sameType?.type_alias || null,
                        descriptor: '',
                        level_framework_value: null,
                        progression_previous: null,
                        progression_next: [],
                        framework_data: []
                    });
                }
            });
        }

        componentState.renderKey++;
        triggerNotification(`Level ${newLevel} added`, 'success');
    }
}

function addNewType() {
    if (!componentState.newTypeName || distinctTypes.value.includes(componentState.newTypeName)) {
        return;
    }

    const typeName = componentState.newTypeName;
    const typeAlias = componentState.newTypeAlias || null;

    if (componentState.levelingConfig.client_framework_config?.framework_value === 'Custom') {
        if (!componentState.customTypes.includes(typeName)) {
            componentState.customTypes.push(typeName);
            componentState.customTypes.sort();
        }

        const levels = componentState.customLevels;
        if (levels.length > 0) {
            levels.forEach(level => {
                const exists = componentState.levelingConfig.client_framework_mapping.some(m =>
                    `${m.level}-${m.type}` === `${level}-${typeName}`
                );

                if (!exists) {
                    // Inherit level_alias from existing mapping with same level
                    const sameLevel = componentState.levelingConfig.client_framework_mapping.find(m => String(m.level) === String(level));
                    componentState.levelingConfig.client_framework_mapping.push({
                        client_level_framework_map_id: null,
                        client_value: null,
                        level: String(level),
                        level_alias: sameLevel?.level_alias || null,
                        type: typeName,
                        type_alias: typeAlias,
                        descriptor: '',
                        level_framework_value: null,
                        progression_previous: null,
                        progression_next: [],
                        framework_data: []
                    });
                }
            });
        }

        componentState.showAddTypeDialog = false;
        componentState.newTypeName = "";
        componentState.newTypeAlias = "";
        componentState.renderKey++;
        triggerNotification(`Type "${typeName}" added`, 'success');
    }
}

function openEditAlias(mode, key) {
    const mappings = componentState.levelingConfig.client_framework_mapping || [];
    let currentAlias = null;

    if (mode === 'track') {
        const match = mappings.find(m => m.type === key);
        currentAlias = match?.type_alias || null;
    } else if (mode === 'level') {
        const match = mappings.find(m => String(m.level) === String(key));
        currentAlias = match?.level_alias || null;
    }

    componentState.editAlias = {
        mode: mode,
        key: key,
        alias: currentAlias || ''
    };
    componentState.showEditAliasDialog = true;
}

function saveAlias() {
    const { mode, key, alias } = componentState.editAlias;
    const trimmedAlias = alias?.trim() || null;

    componentState.levelingConfig.client_framework_mapping.forEach(m => {
        if (mode === 'track' && m.type === key) {
            m.type_alias = trimmedAlias;
        } else if (mode === 'level' && String(m.level) === String(key)) {
            m.level_alias = trimmedAlias;
        }
    });

    componentState.showEditAliasDialog = false;
    componentState.renderKey++;
    triggerNotification(`${mode === 'track' ? 'Track' : 'Level'} alias updated`, 'success');
}

async function saveLevelingData() {
    componentState.savingLeveling = true;

    try {

        let data = {
            client_framework_config: componentState.levelingConfig.client_framework_config,
            client_framework_mapping: componentState.levelingConfig.client_framework_mapping
        }


        const response = await axios.post('admin/saveLevelingData', data);

        if (response.data.Status === 'OK') {
            triggerNotification('Leveling data saved successfully', 'success');

            if (response.data.Data && response.data.Data.client_level_framework_id) {
                componentState.levelingConfig.client_framework_config.client_level_framework_id = response.data.Data.client_level_framework_id;
            }
        } else {
            triggerNotification(response.data.Message || 'Failed to save leveling data', 'error');
        }
    } catch (error) {
        console.error('Error saving leveling data:', error);
        triggerNotification('Error saving leveling data', 'error');
    } finally {
        componentState.savingLeveling = false;
    }
}

async function init() {
    await loadLevellingConfig();
}

function addTemplateConfig() {
    componentState.newConfig.template_configs.push({
        tp_id: null,
        tpa_id: null,
        tmpl_id: null,
        loadingParts: false,
        templateParts: []
    });
}

function removeTemplateConfig(index) {
    if (componentState.newConfig.template_configs.length > 1) {
        componentState.newConfig.template_configs.splice(index, 1);
    }
}

async function onDocTypeChange(index) {
    const config = componentState.newConfig.template_configs[index];

    // Reset dependent fields
    config.tp_id = null;
    config.tpa_id = null;
    config.templateParts = [];

    if (!config.tmpl_id) return;

    // Load template parts for the selected document type
    await loadTemplatePartsForConfig(index);
}

async function loadTemplatePartsForConfig(index) {
    const config = componentState.newConfig.template_configs[index];

    if (!config.tmpl_id) return;

    try {
        config.loadingParts = true;
        const response = await axios.get(`/templateBuilder/templateDetails/${config.tmpl_id}`);

        if (response.data && response.data.Data) {
            const parts = response.data.Data.parts.filter(x => x.tp_active);

            // First pass: create the basic structure
            const templateParts = parts.map(tp => ({
                tp_id: tp.tp_id,
                name: tp.tp_name,
                parent_tp_id: tp.parent_tp_id,
                cardinality: tp.cardinality,
                attributes: tp.attributes || [],
                child_parts: [],
                child_parts_count: 0,
                isTagPart: tp.isTagPart,
                tag_type_id: tp.isTagPart ? tp.attributes[0]?.tag_type_id : null
            }));

            // Second pass: populate child parts and calculate counts
            templateParts.forEach(part => {
                part.child_parts = templateParts.filter(p => p.parent_tp_id === part.tp_id);
                part.child_parts_count = part.child_parts.length;
            });

            config.templateParts = templateParts;
        }
    } catch (error) {
        console.error('Error loading template parts:', error);
        triggerNotification('Error loading template parts: ' + error.message, 'error');
    } finally {
        config.loadingParts = false;
    }
}

function onTemplatePartChange(index) {
    const config = componentState.newConfig.template_configs[index];
    // Reset attribute selection when template part changes
    config.tpa_id = null;
}

function getTemplatePartsForConfig(index) {
    const config = componentState.newConfig.template_configs[index];
    return config.templateParts || [];
}

function getAttributesForConfig(index) {
    const config = componentState.newConfig.template_configs[index];
    if (!config.tp_id || !config.templateParts) return [];

    const selectedPart = config.templateParts.find(p => p.tp_id === config.tp_id);
    return selectedPart?.attributes || [];
}

//#region Track Template Config Helpers
function addTrackTemplateConfig() {
    componentState.newConfig.track_template_configs.push({
        tp_id: null,
        tpa_id: null,
        tmpl_id: null,
        loadingParts: false,
        templateParts: []
    });
}

function removeTrackTemplateConfig(index) {
    if (componentState.newConfig.track_template_configs.length > 1) {
        componentState.newConfig.track_template_configs.splice(index, 1);
    }
}

async function onTrackDocTypeChange(index) {
    const config = componentState.newConfig.track_template_configs[index];
    config.tp_id = null;
    config.tpa_id = null;
    config.templateParts = [];
    if (!config.tmpl_id) return;
    await loadTrackTemplatePartsForConfig(index);
}

async function loadTrackTemplatePartsForConfig(index) {
    const config = componentState.newConfig.track_template_configs[index];
    if (!config.tmpl_id) return;

    try {
        config.loadingParts = true;
        const response = await axios.get(`/templateBuilder/templateDetails/${config.tmpl_id}`);

        if (response.data && response.data.Data) {
            const parts = response.data.Data.parts.filter(x => x.tp_active);

            const templateParts = parts.map(tp => ({
                tp_id: tp.tp_id,
                name: tp.tp_name,
                parent_tp_id: tp.parent_tp_id,
                cardinality: tp.cardinality,
                attributes: tp.attributes || [],
                child_parts: [],
                child_parts_count: 0,
                isTagPart: tp.isTagPart,
                tag_type_id: tp.isTagPart ? tp.attributes[0]?.tag_type_id : null
            }));

            templateParts.forEach(part => {
                part.child_parts = templateParts.filter(p => p.parent_tp_id === part.tp_id);
                part.child_parts_count = part.child_parts.length;
            });

            config.templateParts = templateParts;
        }
    } catch (error) {
        console.error('Error loading track template parts:', error);
        triggerNotification('Error loading track template parts: ' + error.message, 'error');
    } finally {
        config.loadingParts = false;
    }
}

function onTrackTemplatePartChange(index) {
    const config = componentState.newConfig.track_template_configs[index];
    config.tpa_id = null;
}

function getTrackTemplatePartsForConfig(index) {
    const config = componentState.newConfig.track_template_configs[index];
    return config.templateParts || [];
}

function getTrackAttributesForConfig(index) {
    const config = componentState.newConfig.track_template_configs[index];
    if (!config.tp_id || !config.templateParts) return [];

    const selectedPart = config.templateParts.find(p => p.tp_id === config.tp_id);
    return selectedPart?.attributes || [];
}
//#endregion

async function createFrameworkConfig() {
    if (!isConfigValid.value) {
        triggerNotification('Please complete all required fields', 'warning');
        return;
    }
    
    componentState.savingConfig = true;
    
    try {
        // Prepare the configuration data
        const configData = {
            framework_value: componentState.newConfig.framework_value,
            type: componentState.newConfig.type
        };
        
        if (componentState.newConfig.type === 'hr') {
            configData.ht_id = componentState.newConfig.ht_id;
            configData.ht_level = componentState.newConfig.ht_level;
        } else if (componentState.newConfig.type === 'tp_id') {
            // Store template configs as JSON, only keeping tp_id, tpa_id, and tmpl_id
            const cleanedConfigs = componentState.newConfig.template_configs.map(config => ({
                tp_id: config.tp_id,
                tpa_id: config.tpa_id || null,
                tmpl_id: config.tmpl_id
            }));
            configData.template_configs = JSON.stringify(cleanedConfigs);
        }

        // Track source fields
        if (componentState.newConfig.track_enabled && componentState.newConfig.track_type) {
            configData.track_type = componentState.newConfig.track_type;

            if (componentState.newConfig.track_type === 'hr') {
                configData.track_ht_id = componentState.newConfig.track_ht_id;
                configData.track_ht_level = componentState.newConfig.track_ht_level;
            } else if (componentState.newConfig.track_type === 'tp_id') {
                const cleanedTrackConfigs = componentState.newConfig.track_template_configs.map(config => ({
                    tp_id: config.tp_id,
                    tpa_id: config.tpa_id || null,
                    tmpl_id: config.tmpl_id
                }));
                configData.track_template_configs = JSON.stringify(cleanedTrackConfigs);
            }
        } else {
            configData.track_type = null;
            configData.track_ht_id = null;
            configData.track_ht_level = null;
            configData.track_template_configs = null;
        }

        // Reload the configuration to get the framework ID and existing mappings if in edit mode
        let existingFrameworkId = null;
        let existingMappings = [];
        if (componentState.isEditMode) {
            const reloadResponse = await axios.get('admin/levellingConfig');
            if (reloadResponse.data && reloadResponse.data.Status === 'OK' && reloadResponse.data.Data?.client_framework_config) {
                existingFrameworkId = reloadResponse.data.Data.client_framework_config.client_level_framework_id;
                existingMappings = reloadResponse.data.Data.client_framework_mapping || [];
            }
        }

        // Call saveLevelingData with configuration, preserving existing mappings when editing
        const response = await axios.post('admin/saveLevelingData', {
            client_framework_config: {
                ...configData,
                client_level_framework_id: existingFrameworkId // null for new, ID for update
            },
            client_framework_mapping: existingMappings
        });

        if (response.data.Status === 'OK') {
            // Update local state with the new configuration, preserving mappings when editing
            componentState.levelingConfig.client_framework_config = {
                ...configData,
                client_level_framework_id: response.data.Data.client_level_framework_id
            };
            if (!componentState.isEditMode) {
                componentState.levelingConfig.client_framework_mapping = [];
            }

            const message = componentState.isEditMode ? 'Framework configuration updated successfully' : 'Framework configuration created successfully';
            triggerNotification(message, 'success');

            // Clear edit mode and reload full config to get fresh state
            componentState.isEditMode = false;
            await loadLevellingConfig();
        } else {
            triggerNotification(response.data.Message || 'Failed to save framework configuration', 'error');
        }
    } catch (error) {
        console.error('Error saving framework configuration:', error);
        triggerNotification('Error saving framework configuration', 'error');
    } finally {
        componentState.savingConfig = false;
    }
}

async function editConfiguration() {
    const config = componentState.levelingConfig.client_framework_config;

    if (!config) {
        triggerNotification('No configuration to edit', 'error');
        return;
    }

    // Load existing configuration into the form
    componentState.newConfig = {
        framework_value: config.framework_value,
        type: config.type,
        ht_id: config.ht_id || null,
        ht_level: config.ht_level || null,
        template_configs: [],
        track_enabled: !!config.track_type,
        track_type: config.track_type || null,
        track_ht_id: config.track_ht_id || null,
        track_ht_level: config.track_ht_level || null,
        track_template_configs: []
    };

    // Handle template part configurations
    if (config.type === 'tp_id' && config.template_configs) {
        const configs = parsedTemplateConfigs.value;

        if (configs.length > 0) {
            // Load template parts for each configuration
            componentState.newConfig.template_configs = [];

            for (const tplConfig of configs) {
                const newConfig = {
                    tp_id: tplConfig.tp_id,
                    tpa_id: tplConfig.tpa_id || null,
                    tmpl_id: tplConfig.tmpl_id,
                    loadingParts: false,
                    templateParts: []
                };

                componentState.newConfig.template_configs.push(newConfig);

                // Load template parts for this document type
                if (newConfig.tmpl_id) {
                    const index = componentState.newConfig.template_configs.length - 1;
                    await loadTemplatePartsForConfig(index);
                }
            }
        } else {
            componentState.newConfig.template_configs = [{
                tp_id: null,
                tpa_id: null,
                tmpl_id: null,
                loadingParts: false,
                templateParts: []
            }];
        }
    } else {
        componentState.newConfig.template_configs = [{
            tp_id: null,
            tpa_id: null,
            tmpl_id: null,
            loadingParts: false,
            templateParts: []
        }];
    }

    // Handle track template part configurations
    if (config.track_type === 'tp_id' && config.track_template_configs) {
        const trackConfigs = parsedTrackTemplateConfigs.value;

        if (trackConfigs.length > 0) {
            componentState.newConfig.track_template_configs = [];

            for (const tplConfig of trackConfigs) {
                const newConfig = {
                    tp_id: tplConfig.tp_id,
                    tpa_id: tplConfig.tpa_id || null,
                    tmpl_id: tplConfig.tmpl_id,
                    loadingParts: false,
                    templateParts: []
                };

                componentState.newConfig.track_template_configs.push(newConfig);

                if (newConfig.tmpl_id) {
                    const index = componentState.newConfig.track_template_configs.length - 1;
                    await loadTrackTemplatePartsForConfig(index);
                }
            }
        } else {
            componentState.newConfig.track_template_configs = [{
                tp_id: null,
                tpa_id: null,
                tmpl_id: null,
                loadingParts: false,
                templateParts: []
            }];
        }
    } else {
        componentState.newConfig.track_template_configs = [{
            tp_id: null,
            tpa_id: null,
            tmpl_id: null,
            loadingParts: false,
            templateParts: []
        }];
    }

    // Set edit mode flag
    componentState.isEditMode = true;

    // Clear the existing configuration to show the form
    componentState.levelingConfig.client_framework_config = null;

    triggerNotification('Edit mode enabled. Update your configuration and click "Update Framework" to save.', 'info');
}

function resetConfiguration() {
    if (confirm('Are you sure you want to reset the framework configuration? This will remove all existing leveling data.')) {
        componentState.levelingConfig.client_framework_config = null;
        componentState.levelingConfig.client_framework_mapping = [];
        componentState.levelingConfig.full_framework = null;

        // Reset form data
        componentState.newConfig = {
            framework_value: null,
            type: null,
            ht_id: null,
            ht_level: null,
            template_configs: [
                {
                    tp_id: null,
                    tpa_id: null,
                    tmpl_id: null,
                    loadingParts: false,
                    templateParts: []
                }
            ],
            track_enabled: false,
            track_type: null,
            track_ht_id: null,
            track_ht_level: null,
            track_template_configs: [
                {
                    tp_id: null,
                    tpa_id: null,
                    tmpl_id: null,
                    loadingParts: false,
                    templateParts: []
                }
            ]
        };

        // Clear edit mode
        componentState.isEditMode = false;

        triggerNotification('Configuration reset. Please set up your framework again.', 'info');
    }
}

function getProgressionOptions() {
    const types = distinctTypes.value;
    const levels = maxLevel.value;
    const groupedOptions = [];
    
    types.forEach(type => {
        // Add group header
        groupedOptions.push({
            header: type
        });
        
        // Add only valid levels for this type (based on framework validation)
        for (let level = 1; level <= levels; level++) {
            // For RoleMapper framework, check if this level/type combination exists in full_framework
            let isValidCombination = true;
            if (componentState.levelingConfig.client_framework_config?.framework_value === 'RoleMapper' && 
                componentState.levelingConfig.full_framework && 
                Array.isArray(componentState.levelingConfig.full_framework)) {
                const frameworkItem = componentState.levelingConfig.full_framework.find(f => 
                    parseInt(f.level) === level && f.type === type
                );
                isValidCombination = !!frameworkItem;
            }
            
            // Only add valid combinations
            if (isValidCombination) {
                groupedOptions.push({
                    text: `${type}-${level}`,
                    value: `${level}-${type}`,
                    group: type
                });
            }
        }
        
        // Add divider after each group (except the last one)
        if (type !== types[types.length - 1]) {
            groupedOptions.push({
                divider: true
            });
        }
    });
    
    return groupedOptions;
}

function addFrameworkDataItem() {
    if (!componentState.editingMapping.framework_data) {
        componentState.editingMapping.framework_data = [];
    }
    
    componentState.editingMapping.framework_data.push({
        part: '',
        type: '',
        value: ''
    });
}

function removeFrameworkDataItem(index) {
    if (componentState.editingMapping.framework_data) {
        componentState.editingMapping.framework_data.splice(index, 1);
    }
}

//#endregion

//#region Component
watch(() => componentState.levelingConfig.client_framework_mapping, () => {
    console.log('Mapping data changed, forcing recomputation');
    componentState.renderKey++;
}, { deep: true, immediate: false })

onBeforeMount(() => {
    init();
})
//#endregion
</script>

<style scoped lang="scss">
// ...existing styles...

.leveling-container {
  min-height: 400px;
  padding: 0;
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  border-radius: 4px;
  max-height: calc(100vh - 180px);
  overflow: hidden scroll;  
}

.compact-header {
  border-bottom: 1px solid #e0e0e0;
  background: linear-gradient(to bottom, #fafafa, #ffffff);
}

.info-bar {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: white;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}

.info-item {
  display: flex;
  align-items: center;
}

.info-label {
  font-size: 13px;
  font-weight: 600;
  color: #666;
}

// Flow Visualization Styles
.flow-visualization {
  background: #fafafa;
  border-radius: 8px;
  padding: 0;
  overflow: auto;
  width: 100%;
}

.level-column {
  flex-shrink: 0;
  width: 100px;
  background: white;
  border-right: 2px solid #e0e0e0;
  position: sticky;
  left: 0;
  z-index: 10;

  .level-row {
    background: white;
    transition: background 0.2s;

    &:hover {
      background: #f5f5f5;
    }
    
    &.level-row-empty {
      background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
      border: 1px dashed #1976d2;
      margin: 4px;
      border-radius: 4px;
      
      &:hover {
        background: linear-gradient(135deg, #e3f2fd 0%, #f5f7fa 100%);
      }
    }
  }
}

.flow-svg {
  display: block;
  background: white;
  flex-grow: 1;
}

.flow-node {
  transition: all 0.2s ease;

  .node-rect {
    transition: all 0.2s ease;
  }

  &:hover {
    .node-rect {
      filter: brightness(0.95);
    }
  }
}

.empty-state {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;

  .gap-3 {
    gap: 12px;
  }
}

.custom-framework-container {
  .empty-state {
    background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
    border-radius: 4px;
  }
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 4px;
}

.loading-disabled {
  pointer-events: none;
  opacity: 0.7;
}

.initial-config-container {
  max-width: 700px;
  margin: 0 auto;
  padding: 20px 0;
}

.config-header {
  text-align: center;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
}

.config-title {
  font-size: 24px;
  font-weight: 500;
  color: #1976d2;
  margin: 0 0 8px 0;
}

.config-subtitle {
  font-size: 14px;
  color: #666;
  margin: 0;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

.config-form {
  text-align: left;
}

.config-section-card {
  border: 1px solid #e0e0e0 !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08) !important;
  background: #fafbfc !important;
  
  &:hover {
    box-shadow: 0 2px 6px rgba(0,0,0,0.12) !important;
    border-color: #1976d2 !important;
  }
}

.config-section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  margin: 0;
}

.config-subsection-title {
  font-size: 14px;
  font-weight: 500;
  color: #555;
  display: flex;
  align-items: center;
  margin: 0;
}

.subsection-description {
  font-size: 12px;
  color: #666;
  margin: 0;
}

.framework-selection, .data-source-selection {
  .framework-option, .data-source-option {
    .framework-option-content, .data-source-option-content {
      margin-left: 8px;
    }

    
    .framework-option-title, .data-source-option-title {
      font-weight: 600;
      font-size: 14px;
      color: #333;
      margin-bottom: 2px;
      display: flex;
      align-items: center;
    }
    
    .framework-option-description, .data-source-option-description {
      font-size: 12px;
      color: #666;
      line-height: 1.3;
    }
  }
}

.level-field-section {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
  margin-top: 16px;
}

.template-config-card {
  border: 1px solid #e8e8e8 !important;
  background: white !important;
  
  &:hover {
    border-color: #ccc !important;
  }
}

.config-item-title {
  font-size: 13px;
  font-weight: 500;
  color: #555;
  margin: 0;
}

.add-config-btn {
  font-size: 12px;
  text-transform: none;
  letter-spacing: 0;
}

.config-actions {
  text-align: center;
  padding: 20px 0;
  
  .create-button {
    font-size: 16px !important;
    padding: 12px 40px !important;
    font-weight: 500 !important;
    text-transform: none !important;
    letter-spacing: 0 !important;
    min-width: 180px;
  }
}

.hierarchy-item {
  padding: 4px 0;
  
  .hierarchy-item-name {
    font-weight: 500;
    font-size: 14px;
    color: #333;
  }
}

.level-item {
  padding: 4px 0;
  
  .level-item-name {
    font-weight: 500;
    font-size: 14px;
    color: #333;
  }
}

.config-dialog-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
  
  h3 {
    font-size: 20px;
    font-weight: 500;
    color: #333;
    margin: 0;
  }
}

.framework-data-section {
  margin-top: 24px;
  
  .framework-data-header {
    h4 {
      font-size: 16px;
      font-weight: 500;
      color: #333;
      margin: 0;
    }
  }
}

.framework-data-container {
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.framework-data-items-container {
  .framework-data-items-header {
    background: #fff;
    padding: 12px 16px;
    border-bottom: 1px solid #e9ecef;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .items-count {
      font-size: 13px;
      font-weight: 500;
      color: #495057;
    }
  }
}

.framework-data-items-list {
  max-height: 300px;
  overflow-y: auto;
  background: #fff;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f3f5;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c8cd;
    border-radius: 3px;
    
    &:hover {
      background: #a8b2b8;
    }
  }
}

.framework-data-item-row {
  border-bottom: 1px solid #f1f3f4;
  
  &:hover {
    background: #f8f9fb;
  }
  
  &:last-child {
    border-bottom: none;
  }
}

.framework-data-item-content {
  padding: 16px;
}

.framework-data-item-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.item-index {
  background: #e9ecef;
  color: #495057;
  font-weight: 600;
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 4px;
  min-width: 32px;
  text-align: center;
  margin-top: 20px; // Align with field inputs
  flex-shrink: 0;
}

.item-fields {
  display: flex;
  gap: 12px;
  flex: 1;
  align-items: flex-end;
  flex-wrap: wrap;
}

.field-group {
  display: flex;
  flex-direction: column;
  min-width: 120px;
  flex: 1;
  
  &.field-group-wide {
    flex: 2;
    min-width: 200px;
  }
}

.field-label {
  font-size: 11px;
  font-weight: 500;
  color: #6c757d;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.field-input {
  ::v-deep .v-input__control {
    min-height: 32px !important;
  }
  
  ::v-deep .v-text-field__details {
    display: none;
  }
  
  ::v-deep input {
    font-size: 13px;
    padding: 8px 12px !important;
  }
}

.item-actions {
  margin-top: 20px; // Align with field inputs
  flex-shrink: 0;
}

.delete-btn {
  transition: all 0.2s ease;
  
  &:hover {
    background: #fff5f5 !important;
    border: 1px solid #fed7d7;
  }
}

.framework-data-empty-state {
  text-align: center;
  padding: 40px 20px;
  background: #fff;
  
  .empty-text {
    font-size: 14px;
    font-weight: 500;
    color: #6c757d;
    margin: 0 0 4px 0;
  }
  
  .empty-subtext {
    font-size: 12px;
    color: #adb5bd;
    margin: 0;
  }
}

.framework-data-actions {
  padding: 16px;
  text-align: center;
  background: #fff;
  border-top: 1px solid #e9ecef;
}

.add-data-btn {
  font-size: 13px;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
}

.connection-path {
  transition: all 0.3s ease;
  
  &:hover {
    stroke-width: 4 !important;
    opacity: 1 !important;
  }
}

.connection-arrow {
  transition: all 0.3s ease;
}

.connections-layer {
  pointer-events: none; // Prevent interference with node interactions
}

// Responsive adjustments
@media (max-width: 768px) {
  .item-fields {
    flex-direction: column;
    gap: 8px;
  }
  
  .field-group {
    min-width: 100%;
    
    &.field-group-wide {
      flex: 1;
    }
  }
  
  .framework-data-item-header {
    flex-direction: column;
    gap: 12px;
  }
  
  .item-index {
    margin-top: 0;
    align-self: flex-start;
  }
  
  .item-actions {
    margin-top: 0;
    align-self: flex-end;
  }
}
</style>
